import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  UserPlus, 
  Shield, 
  Building2, 
  LogOut, 
  RefreshCw,
  Search,
  MoreVertical,
  Key,
  UserX,
  UserCheck,
  Clock,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Landmark,
  Activity,
  MapPin,
  Wallet,
  Link,
  Copy,
  Network,
  ChevronDown,
  ChevronRight,
  School,
  User,
  GraduationCap,
  Mail,
  Phone,
  BookOpen,
  X,
  Send,
  Printer,
  CreditCard,
  ArrowUpRight,
  ArrowDownLeft,
  Banknote,
  History,
  Paperclip,
  Download,
  BarChart3,
  PieChart,
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  Filter,
  ClipboardList,
  Target
} from 'lucide-react';
import InstallPWA from '../components/InstallPWA';
import DirectorLessonNotes from '../components/DirectorLessonNotes';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function DirectorDashboard() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // User management state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newOfficer, setNewOfficer] = useState({
    name: '',
    email: '',
    temp_password: '',
    role: '',
    permissions: []
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Permission options - all director dashboard tabs available as permissions
  const allPermissions = [
    { id: 'overview', label: 'Overview', description: 'View district overview and statistics' },
    { id: 'circuits', label: 'Circuits/SISOs', description: 'View and manage connected SISOs and circuits' },
    { id: 'applications', label: 'Applications', description: 'Register, file, and manage applications' },
    { id: 'reports', label: 'Reports', description: 'View district reports and analytics' },
    { id: 'users', label: 'Users', description: 'View and manage district users' },
    { id: 'audit', label: 'Audit', description: 'View audit logs' },
    { id: 'wallet', label: 'Wallet', description: 'View wallet and earnings' },
    { id: 'lesson_notes', label: 'Lesson Notes', description: 'Browse and download teacher lesson notes' }
  ];

  const rolePermissions = {
    secretary: allPermissions,
    dd_supervision: allPermissions,
    hrm: allPermissions,
    personnel: allPermissions,
    supervision_team: allPermissions
  };

  // Edit officer state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOfficer, setEditingOfficer] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // DD-Supervision Connect Token state (also used by Director)
  const [connectToken, setConnectToken] = useState(null);
  const [connectedSisos, setConnectedSisos] = useState([]);
  const [loadingToken, setLoadingToken] = useState(false);
  const [loadingSisos, setLoadingSisos] = useState(false);
  
  // Expanded SISO cards state
  const [expandedSisoId, setExpandedSisoId] = useState(null);
  
  // Expanded school cards state (within SISO)
  const [expandedSchoolId, setExpandedSchoolId] = useState(null);
  
  // Profile modal state
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [sisoCircuitData, setSisoCircuitData] = useState({});
  const [loadingCircuitData, setLoadingCircuitData] = useState({});
  const [teacherStats, setTeacherStats] = useState(null);
  const [loadingTeacherStats, setLoadingTeacherStats] = useState(false);

  // DD-Supervision Reports state
  const [ddReportsData, setDdReportsData] = useState(null);
  const [loadingDdReports, setLoadingDdReports] = useState(false);

  // Circuits management state (Director)
  const [circuits, setCircuits] = useState([]);
  const [loadingCircuits, setLoadingCircuits] = useState(false);
  const [showCircuitModal, setShowCircuitModal] = useState(false);
  const [editingCircuit, setEditingCircuit] = useState(null);
  const [newCircuit, setNewCircuit] = useState({ name: '', description: '' });

  // Director Applications state
  const [directorApplications, setDirectorApplications] = useState([]);
  const [loadingDirectorApps, setLoadingDirectorApps] = useState(false);
  const [directorAppFilter, setDirectorAppFilter] = useState('all'); // 'all', 'filed', 'escalated'
  const [directorUnreadCount, setDirectorUnreadCount] = useState(0);

  // Reports state
  const [reportsData, setReportsData] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportType, setReportType] = useState('overview'); // 'overview', 'siso', 'applications', 'schools'

  // Secretary Applications state (for secretary role)
  const [secretaryApplications, setSecretaryApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [unreadAppCount, setUnreadAppCount] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registrationNotes, setRegistrationNotes] = useState('');
  const [processingAppAction, setProcessingAppAction] = useState(false);
  const [applicationDocuments, setApplicationDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentViewTab, setDocumentViewTab] = useState('applicant');

  // Embed signature/stamp into HTML content for display (replaces placeholders with img tags)
  const embedAssetsInHtml = (html, signatureUrl, stampUrl) => {
    if (!html) return html;
    let result = html;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Signature
    if (signatureUrl) {
      const fullSigUrl = signatureUrl.startsWith('http') ? signatureUrl : `${backendUrl}${signatureUrl}`;
      const sigImg = `<div style="margin:10px 0;"><img src="${fullSigUrl}" alt="Signature" style="max-height:70px;max-width:200px;" crossorigin="anonymous" /></div>`;
      let inserted = false;
      const sigPats = [/_{3,}[\s\S]*?\(Signature\)/gi, /\[SIGNATURE_PLACEHOLDER\]/gi, /\[SIGNATURE\]/gi, /<em>\(Signature\)<\/em>/gi, /\(Signature\)/gi];
      for (const pat of sigPats) {
        if (pat.test(result)) {
          if (!inserted) { result = result.replace(pat, sigImg); inserted = true; }
          result = result.replace(pat, '');
        }
      }
    }
    // Stamp
    if (stampUrl) {
      const fullStampUrl = stampUrl.startsWith('http') ? stampUrl : `${backendUrl}${stampUrl}`;
      const stampImg = `<div style="margin:5px 0;"><img src="${fullStampUrl}" alt="Official Stamp" style="max-height:90px;max-width:90px;" crossorigin="anonymous" /></div>`;
      let stampInserted = false;
      const stampPats = [/\[STAMP_PLACEHOLDER\]/gi, /\[STAMP\]/gi, /<em>\(Stamp\)<\/em>/gi, /\(Official Stamp\)/gi];
      for (const pat of stampPats) {
        if (pat.test(result)) {
          if (!stampInserted) { result = result.replace(pat, stampImg); stampInserted = true; }
          result = result.replace(pat, '');
        }
      }
    }
    // Clean up remaining placeholders if no assets available
    if (!signatureUrl) result = result.replace(/\[SIGNATURE_PLACEHOLDER\]/gi, '').replace(/\[SIGNATURE\]/gi, '');
    if (!stampUrl) result = result.replace(/\[STAMP_PLACEHOLDER\]/gi, '').replace(/\[STAMP\]/gi, '');
    return result;
  };

  // Fetch an image URL and return as base64 data URI (for print preview)
  const fetchImageAsBase64 = async (url) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = url.startsWith('http') ? url : `${backendUrl}${url}`;
      const response = await authenticatedFetch(fullUrl);
      if (!response.ok) return null;
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  // Embed assets as base64 data URIs for print (images load reliably in print window)
  const embedAssetsForPrint = async (html, signatureUrl, stampUrl) => {
    if (!html) return html;
    let result = html;
    if (signatureUrl) {
      const sigB64 = await fetchImageAsBase64(signatureUrl);
      if (sigB64) {
        const sigImg = `<div style="margin:10px 0;"><img src="${sigB64}" alt="Signature" style="max-height:70px;max-width:200px;" /></div>`;
        let inserted = false;
        for (const pat of [/_{3,}[\s\S]*?\(Signature\)/gi, /\[SIGNATURE_PLACEHOLDER\]/gi, /\[SIGNATURE\]/gi, /<em>\(Signature\)<\/em>/gi, /\(Signature\)/gi]) {
          if (pat.test(result)) { if (!inserted) { result = result.replace(pat, sigImg); inserted = true; } result = result.replace(pat, ''); }
        }
        // Also replace any existing <img> with backend URLs for signature
        result = result.replace(/(<img[^>]*alt="Signature"[^>]*src=")https?:\/\/[^"]+(")/gi, `$1${sigB64}$2`);
      }
    }
    if (stampUrl) {
      const stampB64 = await fetchImageAsBase64(stampUrl);
      if (stampB64) {
        const stampImg = `<div style="margin:5px 0;"><img src="${stampB64}" alt="Official Stamp" style="max-height:90px;max-width:90px;" /></div>`;
        let stampInserted = false;
        for (const pat of [/\[STAMP_PLACEHOLDER\]/gi, /\[STAMP\]/gi, /<em>\(Stamp\)<\/em>/gi, /\(Official Stamp\)/gi]) {
          if (pat.test(result)) { if (!stampInserted) { result = result.replace(pat, stampImg); stampInserted = true; } result = result.replace(pat, ''); }
        }
        result = result.replace(/(<img[^>]*alt="Official Stamp"[^>]*src=")https?:\/\/[^"]+(")/gi, `$1${stampB64}$2`);
      }
    }
    if (!signatureUrl) result = result.replace(/\[SIGNATURE_PLACEHOLDER\]/gi, '').replace(/\[SIGNATURE\]/gi, '');
    if (!stampUrl) result = result.replace(/\[STAMP_PLACEHOLDER\]/gi, '').replace(/\[STAMP\]/gi, '');
    return result;
  };

  // Function to render formatted letters (handles formatting tags)
  const renderFormattedLetter = (letter, signatureUrl = null, stampUrl = null) => {
    if (!letter) return <p className="text-gray-500 italic">No content available</p>;
    
    const lines = letter.split('\n');
    const elements = [];
    let currentAlignment = 'left';
    
    lines.forEach((line, index) => {
      let processedLine = line;
      let lineAlignment = currentAlignment;
      let isHeading = false;
      
      // Check for alignment markers
      if (processedLine.includes('[RIGHT]')) {
        lineAlignment = 'right';
        currentAlignment = 'right';
        processedLine = processedLine.replace(/\[RIGHT\]\s*/g, '');
      } else if (processedLine.includes('[LEFT]')) {
        lineAlignment = 'left';
        currentAlignment = 'left';
        processedLine = processedLine.replace(/\[LEFT\]\s*/g, '');
      } else if (processedLine.includes('[CENTER]')) {
        lineAlignment = 'center';
        currentAlignment = 'center';
        processedLine = processedLine.replace(/\[CENTER\]\s*/g, '');
      }
      
      if (processedLine.includes('[BOLD_UNDERLINE]') || processedLine.includes('[/BOLD_UNDERLINE]')) {
        isHeading = processedLine.includes('[BOLD_UNDERLINE]');
        processedLine = processedLine.replace(/\[\/?BOLD_UNDERLINE\]/g, '');
      }
      
      // Handle signature placeholder
      if (processedLine.includes('[SIGNATURE_PLACEHOLDER]') || processedLine.includes('[SIGNATURE]')) {
        if (signatureUrl) {
          elements.push(
            <div key={index} className="flex justify-end mt-4 mb-2">
              <img src={`${API_BASE}${signatureUrl}`} alt="Signature" className="h-16 w-auto object-contain" />
            </div>
          );
        }
        return;
      }
      
      // Handle stamp placeholder
      if (processedLine.includes('[STAMP_PLACEHOLDER]') || processedLine.includes('[STAMP]')) {
        if (stampUrl) {
          elements.push(
            <div key={index} className="flex justify-end my-2">
              <img src={`${API_BASE}${stampUrl}`} alt="Stamp" className="h-20 w-auto object-contain" />
            </div>
          );
        }
        return;
      }
      
      // Remove remaining tags
      processedLine = processedLine
        .replace(/```/g, '')
        .replace(/`plaintext`?/gi, '')
        .replace(/plaintext/gi, '')
        .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
        .replace(/\[\/?CENTER\]/g, '')
        .replace(/\[\/?RIGHT\]/g, '')
        .replace(/\[\/?LEFT\]/g, '')
        .replace(/\[\/?BOLD\]/g, '')
        .replace(/\[\/?UNDERLINE\]/g, '')
        .trim();
      
      if (!processedLine) {
        elements.push(<div key={index} className="h-3"></div>);
        return;
      }
      
      // Determine alignment class
      let containerClass = 'flex';
      if (lineAlignment === 'right') containerClass = 'flex justify-end';
      else if (lineAlignment === 'center') containerClass = 'flex justify-center';
      else containerClass = 'flex justify-start';
      
      if (isHeading) {
        elements.push(
          <div key={index} className="flex justify-center my-4">
            <span style={{ fontWeight: 'bold', textDecoration: 'underline', textUnderlineOffset: '4px', textTransform: 'uppercase' }}>
              {processedLine}
            </span>
          </div>
        );
      } else {
        elements.push(
          <div key={index} className={containerClass}>
            <span>{processedLine}</span>
          </div>
        );
      }
    });
    
    return elements;
  };

  // Wallet Manager state
  const [walletData, setWalletData] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [processingWithdrawal, setProcessingWithdrawal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(false);
  const [accountDetails, setAccountDetails] = useState({
    mobile_money_number: '',
    mobile_money_network: '',
    account_name: ''
  });
  const [savingAccount, setSavingAccount] = useState(false);

  const authenticatedFetch = async (url, options = {}) => {
    const token = localStorage.getItem('auth_token');
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/dashboard`);
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        toast.error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/audit-logs`);
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Load audit logs error:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // DD-Supervision Reports functions
  const loadDdReportsData = async () => {
    if (user?.role !== 'dd_supervision') return;
    setLoadingDdReports(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/dd-supervision/reports`);
      if (response.ok) {
        const data = await response.json();
        setDdReportsData(data);
      }
    } catch (error) {
      console.error('Load reports error:', error);
      toast.error('Failed to load reports data');
    } finally {
      setLoadingDdReports(false);
    }
  };


  // Wallet Manager functions
  const loadWallet = async () => {
    setLoadingWallet(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/wallet`);
      if (response.ok) {
        const data = await response.json();
        setWalletData(data);
        setAccountDetails({
          mobile_money_number: data.wallet.mobile_money_number || '',
          mobile_money_network: data.wallet.mobile_money_network || '',
          account_name: data.wallet.account_name || ''
        });
      }
    } catch (error) {
      console.error('Load wallet error:', error);
      toast.error('Failed to load wallet data');
    } finally {
      setLoadingWallet(false);
    }
  };

  const saveAccountDetails = async () => {
    if (!accountDetails.mobile_money_number || !accountDetails.mobile_money_network || !accountDetails.account_name) {
      toast.error('Please fill in all account details');
      return;
    }
    
    setSavingAccount(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/wallet/account`, {
        method: 'PUT',
        body: JSON.stringify(accountDetails)
      });
      
      if (response.ok) {
        toast.success('Account details saved successfully');
        setEditingAccount(false);
        loadWallet();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save account details');
      }
    } catch (error) {
      console.error('Save account error:', error);
      toast.error('Failed to save account details');
    } finally {
      setSavingAccount(false);
    }
  };

  const requestWithdrawal = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!walletData?.wallet?.mobile_money_number) {
      toast.error('Please set up your mobile money account first');
      setEditingAccount(true);
      return;
    }
    
    setProcessingWithdrawal(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/wallet/withdraw`, {
        method: 'POST',
        body: JSON.stringify({ amount })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setWithdrawAmount('');
        loadWallet();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Withdrawal request failed');
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      toast.error('Failed to process withdrawal request');
    } finally {
      setProcessingWithdrawal(false);
    }
  };

  // DD-Supervision: Load connect token
  const loadConnectToken = async () => {
    setLoadingToken(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/dd-supervision/connect-token`);
      if (response.ok) {
        const data = await response.json();
        setConnectToken(data);
      }
    } catch (error) {
      console.error('Load connect token error:', error);
    } finally {
      setLoadingToken(false);
    }
  };

  // DD-Supervision: Load connected SISOs
  const loadConnectedSisos = async () => {
    setLoadingSisos(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/dd-supervision/connected-sisos`);
      if (response.ok) {
        const data = await response.json();
        setConnectedSisos(data.connected_sisos || []);
      }
    } catch (error) {
      console.error('Load connected SISOs error:', error);
    } finally {
      setLoadingSisos(false);
    }
  };

  // DD-Supervision: Regenerate connect token
  const regenerateToken = async () => {
    if (!window.confirm('Are you sure you want to regenerate the connect token? This will invalidate the old token.')) {
      return;
    }
    
    setProcessingAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/dd-supervision/regenerate-token`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnectToken(prev => ({ ...prev, connect_token: data.connect_token }));
        toast.success('Connect token regenerated successfully');
      } else {
        toast.error('Failed to regenerate token');
      }
    } catch (error) {
      toast.error('Error regenerating token');
    } finally {
      setProcessingAction(false);
    }
  };

  // Copy token to clipboard
  const copyTokenToClipboard = () => {
    if (connectToken?.connect_token) {
      navigator.clipboard.writeText(connectToken.connect_token);
      toast.success('Connect token copied to clipboard');
    }
  };

  // Toggle expanded SISO card and load circuit data
  const toggleSisoExpand = async (sisoId) => {
    if (expandedSisoId === sisoId) {
      // Collapse if already expanded
      setExpandedSisoId(null);
    } else {
      // Expand and load data if not already loaded
      setExpandedSisoId(sisoId);
      
      if (!sisoCircuitData[sisoId]) {
        setLoadingCircuitData(prev => ({ ...prev, [sisoId]: true }));
        try {
          const response = await authenticatedFetch(`${API_BASE}/api/dd-supervision/siso-circuit-data/${sisoId}`);
          if (response.ok) {
            const data = await response.json();
            setSisoCircuitData(prev => ({ ...prev, [sisoId]: data }));
          } else {
            toast.error('Failed to load circuit data');
          }
        } catch (error) {
          console.error('Load circuit data error:', error);
          toast.error('Error loading circuit data');
        } finally {
          setLoadingCircuitData(prev => ({ ...prev, [sisoId]: false }));
        }
      }
    }
  };

  // Refresh circuit data for a specific SISO
  const refreshCircuitData = async (sisoId) => {
    setLoadingCircuitData(prev => ({ ...prev, [sisoId]: true }));
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/dd-supervision/siso-circuit-data/${sisoId}`);
      if (response.ok) {
        const data = await response.json();
        setSisoCircuitData(prev => ({ ...prev, [sisoId]: data }));
        toast.success('Circuit data refreshed');
      }
    } catch (error) {
      toast.error('Error refreshing circuit data');
    } finally {
      setLoadingCircuitData(prev => ({ ...prev, [sisoId]: false }));
    }
  };

  // Toggle expanded school card
  const toggleSchoolExpand = (schoolId) => {
    setExpandedSchoolId(expandedSchoolId === schoolId ? null : schoolId);
  };

  // View full profile
  const viewProfile = async (person, type) => {
    setSelectedProfile({ ...person, type });
    setShowProfileModal(true);
    setTeacherStats(null);
    
    // If it's a teacher and user is DD-Supervision, fetch teacher stats
    if (type === 'teacher' && user?.role === 'dd_supervision' && person?.id) {
      setLoadingTeacherStats(true);
      try {
        const response = await authenticatedFetch(`${API_BASE}/api/dd-supervision/teacher-stats/${person.id}`);
        if (response.ok) {
          const data = await response.json();
          setTeacherStats(data);
        }
      } catch (error) {
        console.error('Error fetching teacher stats:', error);
      } finally {
        setLoadingTeacherStats(false);
      }
    }
  };

  // Close profile modal
  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedProfile(null);
    setTeacherStats(null);
  };

  // ============ SECRETARY APPLICATIONS FUNCTIONS ============
  
  // Load secretary applications
  const loadSecretaryApplications = async () => {
    if (user?.role !== 'secretary') return;
    
    setLoadingApplications(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/secretary/applications`);
      if (response.ok) {
        const data = await response.json();
        setSecretaryApplications(data.applications || []);
        setUnreadAppCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoadingApplications(false);
    }
  };

  // Mark application as read (handles both secretary and director)
  const markApplicationRead = async (app) => {
    try {
      if (user?.role === 'director') {
        if (app.director_read) return;
        await authenticatedFetch(`${API_BASE}/api/director/application/${app.id}/read`, {
          method: 'PUT',
          body: JSON.stringify({ source: app.source })
        });
        setDirectorApplications(prev =>
          prev.map(a => a.id === app.id ? {...a, director_read: true} : a)
        );
        setDirectorUnreadCount(prev => Math.max(0, prev - 1));
      } else if (user?.role === 'secretary') {
        if (app.secretary_read) return;
        await authenticatedFetch(`${API_BASE}/api/secretary/application/${app.id}/read`, {
          method: 'PUT',
          body: JSON.stringify({ source: app.source })
        });
        setSecretaryApplications(prev => 
          prev.map(a => a.id === app.id ? {...a, secretary_read: true} : a)
        );
        setUnreadAppCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking read:', error);
    }
  };

  // View application details with full documents
  const viewApplication = async (app) => {
    markApplicationRead(app);
    setSelectedApplication(app);
    setDocumentViewTab('applicant');
    setShowApplicationModal(true);
    
    // Load full document details
    try {
      setLoadingDocuments(true);
      const response = await authenticatedFetch(`${API_BASE}/api/secretary/application/${app.id}/export?source=${app.source}`);
      if (response.ok) {
        const data = await response.json();
        setApplicationDocuments(data);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load full documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Open register modal
  const openRegisterModal = (app) => {
    setSelectedApplication(app);
    setRegistrationNumber('');
    setRegistrationNotes('');
    setShowRegisterModal(true);
  };

  // Register application
  const registerApplication = async () => {
    if (!selectedApplication || !registrationNumber.trim()) {
      toast.error('Please enter a registration number');
      return;
    }
    
    setProcessingAppAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/secretary/application/${selectedApplication.id}/register`, {
        method: 'PUT',
        body: JSON.stringify({
          source: selectedApplication.source,
          registration_number: registrationNumber.trim(),
          notes: registrationNotes.trim()
        })
      });
      
      if (response.ok) {
        toast.success('Application registered successfully');
        setShowRegisterModal(false);
        setShowApplicationModal(false);
        loadSecretaryApplications();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to register');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to register application');
    } finally {
      setProcessingAppAction(false);
    }
  };

  // File application to director
  const fileToDirector = async (app) => {
    if (app.status !== 'registered_by_secretary') {
      toast.error('Application must be registered first');
      return;
    }
    
    setProcessingAppAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/secretary/application/${app.id}/file-to-director`, {
        method: 'PUT',
        body: JSON.stringify({ source: app.source })
      });
      
      if (response.ok) {
        toast.success('Application filed to director');
        setShowApplicationModal(false);
        loadSecretaryApplications();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to file');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to file application');
    } finally {
      setProcessingAppAction(false);
    }
  };

  // Export/Print application
  const exportApplication = async (app) => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/secretary/application/${app.id}/export?source=${app.source}`);
      if (response.ok) {
        const data = await response.json();
        
        // Create printable content
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <html>
            <head>
              <title>Application - ${data.application.application_type || 'Unknown'}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
                .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
                .section { margin-bottom: 20px; }
                .section-title { font-weight: bold; color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px; margin-bottom: 10px; }
                .field { margin: 8px 0; }
                .label { font-weight: bold; color: #666; }
                .memo-content { background: #f5f5f5; padding: 15px; border-radius: 5px; white-space: pre-wrap; }
                @media print { button { display: none; } }
              </style>
            </head>
            <body>
              <div class="header">
                <h2>APPLICATION FOR ${(data.application.application_type || '').replace(/_/g, ' ').toUpperCase()}</h2>
                <p>Registration No: ${data.application.registration_number || 'Not Registered'}</p>
                <p>Escalation Date: ${data.application.escalation_date ? new Date(data.application.escalation_date).toLocaleDateString() : 'N/A'}</p>
              </div>
              
              <div class="section">
                <div class="section-title">Applicant Information</div>
                <div class="field"><span class="label">Name:</span> ${data.applicant?.name || 'N/A'}</div>
                <div class="field"><span class="label">School:</span> ${data.applicant?.school || 'N/A'}</div>
                <div class="field"><span class="label">File Number:</span> ${data.applicant?.file_number || 'N/A'}</div>
                <div class="field"><span class="label">Rank:</span> ${data.applicant?.rank || 'N/A'}</div>
                <div class="field"><span class="label">Email:</span> ${data.applicant?.email || 'N/A'}</div>
                <div class="field"><span class="label">Phone:</span> ${data.applicant?.phone || 'N/A'}</div>
              </div>
              
              <div class="section">
                <div class="section-title">Escalation Details</div>
                <div class="field"><span class="label">Escalated By:</span> ${data.escalated_by?.name || 'N/A'}</div>
                <div class="field"><span class="label">Status:</span> ${data.application.status}</div>
              </div>
              
              <div class="section">
                <div class="section-title">Application Content</div>
                <div class="memo-content">${data.application.siso_memo || data.application.escalation_memo || data.application.plain_text || 'No content'}</div>
              </div>
              
              <button onclick="window.print()" style="padding: 10px 20px; background: #2563eb; color: white; border: none; border-radius: 5px; cursor: pointer; margin-top: 20px;">
                Print Application
              </button>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    } catch (error) {
      toast.error('Failed to export application');
    }
  };

  // ============ DIRECTOR CIRCUITS FUNCTIONS ============
  
  const loadCircuits = async () => {
    if (user?.role !== 'director') return;
    
    setLoadingCircuits(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/circuits`);
      if (response.ok) {
        const data = await response.json();
        setCircuits(data.circuits || []);
      }
    } catch (error) {
      console.error('Error loading circuits:', error);
      toast.error('Failed to load circuits');
    } finally {
      setLoadingCircuits(false);
    }
  };

  const saveCircuit = async () => {
    if (!newCircuit.name.trim()) {
      toast.error('Circuit name is required');
      return;
    }
    
    setProcessingAction(true);
    try {
      const endpoint = editingCircuit 
        ? `${API_BASE}/api/director/circuits/${editingCircuit.id}`
        : `${API_BASE}/api/director/circuits`;
      
      const response = await authenticatedFetch(endpoint, {
        method: editingCircuit ? 'PUT' : 'POST',
        body: JSON.stringify(newCircuit)
      });
      
      if (response.ok) {
        toast.success(editingCircuit ? 'Circuit updated' : 'Circuit created');
        setShowCircuitModal(false);
        setEditingCircuit(null);
        setNewCircuit({ name: '', description: '' });
        loadCircuits();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save circuit');
      }
    } catch (error) {
      toast.error('Failed to save circuit');
    } finally {
      setProcessingAction(false);
    }
  };

  const deleteCircuit = async (circuitId) => {
    if (!window.confirm('Are you sure you want to delete this circuit?')) return;
    
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/circuits/${circuitId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Circuit deleted');
        loadCircuits();
      } else {
        toast.error('Failed to delete circuit');
      }
    } catch (error) {
      toast.error('Failed to delete circuit');
    }
  };

  // ============ DIRECTOR CONNECT TOKEN FUNCTIONS ============
  
  const loadDirectorConnectToken = async () => {
    if (user?.role !== 'director') return;
    
    setLoadingToken(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/connect-token`);
      if (response.ok) {
        const data = await response.json();
        setConnectToken(data);
      }
    } catch (error) {
      console.error('Load director connect token error:', error);
    } finally {
      setLoadingToken(false);
    }
  };

  const regenerateDirectorToken = async () => {
    if (!window.confirm('Regenerate token? SISOs will need to reconnect with the new token.')) return;
    
    setProcessingAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/regenerate-token`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnectToken(prev => ({ ...prev, connect_token: data.connect_token }));
        toast.success('Token regenerated successfully');
      } else {
        toast.error('Failed to regenerate token');
      }
    } catch (error) {
      toast.error('Failed to regenerate token');
    } finally {
      setProcessingAction(false);
    }
  };

  const copyToken = () => {
    if (connectToken?.connect_token) {
      navigator.clipboard.writeText(connectToken.connect_token);
      toast.success('Token copied to clipboard');
    }
  };

  // ============ DIRECTOR APPLICATIONS FUNCTIONS ============
  
  const loadDirectorApplications = async () => {
    if (user?.role !== 'director') return;
    
    setLoadingDirectorApps(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/applications?filter=${directorAppFilter}`);
      if (response.ok) {
        const data = await response.json();
        setDirectorApplications(data.applications || []);
        setDirectorUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error loading director applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoadingDirectorApps(false);
    }
  };

  // ============ REPORTS FUNCTIONS ============
  
  const loadReports = async () => {
    if (user?.role !== 'director') return;
    
    setLoadingReports(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/reports?type=${reportType}`);
      if (response.ok) {
        const data = await response.json();
        setReportsData(data);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  // Load unread application count for Secretary on initial load (for badge display)
  useEffect(() => {
    if (user?.role === 'secretary') {
      loadSecretaryApplications();
    }
    // Load data for Director on initial load
    if (user?.role === 'director') {
      loadDirectorConnectToken();
      loadDirectorApplications();
    }
  }, [user?.role]);

  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditLogs();
    }
    // Load connect token for DD-Supervision users - they can only view, not generate
    if (activeTab === 'sisos' && user?.role === 'dd_supervision') {
      // DD-Supervision can only view connected SISOs, not manage tokens
      loadConnectedSisos();
    }
    // Load circuits for Director
    if (activeTab === 'circuits' && user?.role === 'director') {
      loadCircuits();
      loadConnectedSisos(); // To show connected SISOs in circuits
    }
    // Load applications for Director
    if (activeTab === 'director-applications' && user?.role === 'director') {
      loadDirectorApplications();
    }
    // Load reports for Director
    if (activeTab === 'reports' && user?.role === 'director') {
      loadReports();
    }
    // Load reports for DD-Supervision
    if (activeTab === 'reports' && user?.role === 'dd_supervision') {
      loadDdReportsData();
    }
    // Refresh applications for Secretary users when tab becomes active
    if (activeTab === 'applications' && user?.role === 'secretary') {
      loadSecretaryApplications();
    }
    // Load wallet for Director users
    if (activeTab === 'wallet' && user?.role === 'director') {
      loadWallet();
    }
  }, [activeTab, user?.role, directorAppFilter, reportType]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const createSubOfficer = async () => {
    if (!newOfficer.name || !newOfficer.email || !newOfficer.temp_password || !newOfficer.role) {
      toast.error('Please fill all fields');
      return;
    }
    
    if (!newOfficer.permissions || newOfficer.permissions.length === 0) {
      toast.error('Please assign at least one permission');
      return;
    }
    
    setProcessingAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/create-sub-officer`, {
        method: 'POST',
        body: JSON.stringify(newOfficer)
      });
      
      if (response.ok) {
        toast.success('Sub-officer created successfully');
        setShowCreateModal(false);
        setNewOfficer({ name: '', email: '', temp_password: '', role: '', permissions: [] });
        loadDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create sub-officer');
      }
    } catch (error) {
      toast.error('Error creating sub-officer');
    } finally {
      setProcessingAction(false);
    }
  };

  // Edit sub-officer
  const editSubOfficer = async () => {
    if (!editingOfficer || !editingOfficer.name || !editingOfficer.email) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (!editingOfficer.permissions || editingOfficer.permissions.length === 0) {
      toast.error('Please assign at least one permission');
      return;
    }
    
    setProcessingAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/sub-officer/${editingOfficer.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editingOfficer.name,
          email: editingOfficer.email,
          permissions: editingOfficer.permissions
        })
      });
      
      if (response.ok) {
        toast.success('Sub-officer updated successfully');
        setShowEditModal(false);
        setEditingOfficer(null);
        loadDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update sub-officer');
      }
    } catch (error) {
      toast.error('Error updating sub-officer');
    } finally {
      setProcessingAction(false);
    }
  };

  // Delete sub-officer
  const deleteSubOfficer = async (officerId, officerName) => {
    if (!window.confirm(`Are you sure you want to delete ${officerName}? This action cannot be undone.`)) {
      return;
    }
    
    setProcessingAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/sub-officer/${officerId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Sub-officer deleted successfully');
        loadDashboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete sub-officer');
      }
    } catch (error) {
      toast.error('Error deleting sub-officer');
    } finally {
      setProcessingAction(false);
    }
  };

  const toggleUserStatus = async (userId) => {
    setProcessingAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/toggle-user-status/${userId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        loadDashboard();
      } else {
        toast.error('Failed to update user status');
      }
    } catch (error) {
      toast.error('Error updating user status');
    } finally {
      setProcessingAction(false);
    }
  };

  const resetUserPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setProcessingAction(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/api/director/reset-password/${selectedUser.id}`, {
        method: 'POST',
        body: JSON.stringify({ new_password: newPassword })
      });
      
      if (response.ok) {
        toast.success('Password reset successfully');
        setShowResetPasswordModal(false);
        setSelectedUser(null);
        setNewPassword('');
        loadDashboard();
      } else {
        toast.error('Failed to reset password');
      }
    } catch (error) {
      toast.error('Error resetting password');
    } finally {
      setProcessingAction(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'director': return 'bg-orange-100 text-orange-700';
      case 'dd_supervision': return 'bg-purple-100 text-purple-700';
      case 'secretary': return 'bg-blue-100 text-blue-700';
      case 'hrm': return 'bg-teal-100 text-teal-700';
      case 'personnel': return 'bg-cyan-100 text-cyan-700';
      case 'supervision_team': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'director': return 'Director';
      case 'dd_supervision': return 'Head Of Supervision';
      case 'secretary': return 'Secretary';
      case 'hrm': return 'HRM';
      case 'personnel': return 'Personnel';
      case 'supervision_team': return 'Supervision Team Member';
      default: return role;
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created_user': return <UserPlus className="h-4 w-4 text-green-600" />;
      case 'disabled_user': return <UserX className="h-4 w-4 text-red-600" />;
      case 'enabled_user': return <UserCheck className="h-4 w-4 text-green-600" />;
      case 'reset_password': return <Key className="h-4 w-4 text-orange-600" />;
      default: return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Director Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
              <Landmark className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">District Directorate</h1>
              <p className="text-xs text-gray-500">
                {dashboardData?.director?.district_name} • {dashboardData?.director?.region_name}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{dashboardData?.director?.name}</p>
              <Badge className={getRoleBadgeColor(dashboardData?.director?.role)}>
                {getRoleDisplayName(dashboardData?.director?.role)}
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-600 hover:text-red-600">
              <LogOut className="h-4 w-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* PWA Install Prompt */}
        <InstallPWA />
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className={`bg-white shadow-sm flex-wrap min-w-max sm:min-w-0 ${user?.role === 'dd_supervision' ? 'gap-2 p-2' : ''}`}>
            <TabsTrigger 
              value="overview" 
              className={user?.role === 'dd_supervision' 
                ? "flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=inactive]:bg-orange-100 data-[state=inactive]:text-orange-700 rounded-lg font-medium transition-all"
                : "flex items-center gap-2"
              }
            >
              <Building2 className={user?.role === 'dd_supervision' ? "h-5 w-5" : "h-4 w-4"} />
              Overview
            </TabsTrigger>
            {user?.role === 'director' && (
              <>
                <TabsTrigger value="circuits" className="flex items-center gap-2">
                  <Network className="h-4 w-4" />
                  Circuits/SISOs
                </TabsTrigger>
                <TabsTrigger value="director-applications" className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Application Manager
                  {directorUnreadCount > 0 && (
                    <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                      {directorUnreadCount > 9 ? '9+' : directorUnreadCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="reports" className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Reports
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Users
                </TabsTrigger>
                <TabsTrigger value="audit" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Audit
                </TabsTrigger>
              </>
            )}
            {user?.role === 'dd_supervision' && (
              <>
                <TabsTrigger 
                  value="sisos" 
                  className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=inactive]:bg-purple-100 data-[state=inactive]:text-purple-700 rounded-lg font-medium transition-all"
                >
                  <Network className="h-5 w-5" />
                  SISO Connections
                </TabsTrigger>
                <TabsTrigger 
                  value="reports" 
                  className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:bg-blue-100 data-[state=inactive]:text-blue-700 rounded-lg font-medium transition-all"
                >
                  <BarChart3 className="h-5 w-5" />
                  Reports
                </TabsTrigger>
              </>
            )}
            {user?.role === 'secretary' && (
              <TabsTrigger value="applications" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Applications
                {unreadAppCount > 0 && (
                  <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                    {unreadAppCount > 9 ? '9+' : unreadAppCount}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="wallet" 
              className={user?.role === 'dd_supervision' 
                ? "flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=inactive]:bg-green-100 data-[state=inactive]:text-green-700 rounded-lg font-medium transition-all"
                : "flex items-center gap-2"
              }
            >
              <Wallet className={user?.role === 'dd_supervision' ? "h-5 w-5" : "h-4 w-4"} />
              Wallet
            </TabsTrigger>
            <TabsTrigger value="lesson_notes" className="flex items-center gap-2" data-testid="director-lesson-notes-tab">
              <BookOpen className="h-4 w-4" />
              Lesson Notes
            </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Stats Cards */}
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">SISOs</p>
                      <p className="text-3xl font-bold">{dashboardData?.stats?.siso_count || 0}</p>
                    </div>
                    <Users className="h-10 w-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Headteachers</p>
                      <p className="text-3xl font-bold">{dashboardData?.stats?.headteacher_count || 0}</p>
                    </div>
                    <Building2 className="h-10 w-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Teachers</p>
                      <p className="text-3xl font-bold">{dashboardData?.stats?.teacher_count || 0}</p>
                    </div>
                    <Users className="h-10 w-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Sub-Officers</p>
                      <p className="text-3xl font-bold">{dashboardData?.stats?.sub_officers_count || 0}</p>
                    </div>
                    <Shield className="h-10 w-10 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* District Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-orange-600" />
                  District Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">District</p>
                    <p className="text-lg font-semibold">{dashboardData?.director?.district_name}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Region</p>
                    <p className="text-lg font-semibold">{dashboardData?.director?.region_name}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Director Email</p>
                    <p className="text-lg font-semibold">{dashboardData?.director?.email}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Role</p>
                    <Badge className={getRoleBadgeColor(dashboardData?.director?.role)}>
                      {getRoleDisplayName(dashboardData?.director?.role)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-orange-600" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData?.recent_audit_logs?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.recent_audit_logs.slice(0, 5).map((log) => (
                      <div key={log.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        {getActionIcon(log.action)}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{log.details}</p>
                          <p className="text-xs text-gray-500">
                            {log.target_user_name} ({log.target_user_email})
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No recent activity</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Circuits/SISOs Tab (Director Only) */}
          {user?.role === 'director' && (
            <TabsContent value="circuits">
              <div className="space-y-6">
                {/* Connect Token Card */}
                <Card className="border-2 border-orange-200 bg-orange-50/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-orange-700">
                          <Link className="h-5 w-5" />
                          District Connect Token
                        </CardTitle>
                        <CardDescription>
                          Share this token with SISOs to connect their circuits to your directorate
                        </CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={loadDirectorConnectToken} disabled={loadingToken}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loadingToken ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingToken ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
                      </div>
                    ) : connectToken?.connect_token ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-white border-2 border-orange-300 rounded-lg p-3 font-mono text-sm break-all">
                            {connectToken.connect_token}
                          </div>
                          <Button onClick={copyToken} variant="outline" className="shrink-0">
                            <Copy className="h-4 w-4 mr-2" />
                            Copy
                          </Button>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">
                            Connected SISOs: <strong className="text-orange-600">{connectToken.connected_sisos_count || 0}</strong>
                          </span>
                          <Button variant="ghost" size="sm" onClick={regenerateDirectorToken} disabled={processingAction} className="text-red-600 hover:text-red-700">
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Regenerate Token
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-gray-500">No token generated yet</p>
                        <Button onClick={loadDirectorConnectToken} className="mt-2">
                          Generate Token
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Circuits Management */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Network className="h-5 w-5 text-orange-600" />
                        Circuits in District
                      </CardTitle>
                      <CardDescription>
                        Manage circuits and view connected SISOs
                      </CardDescription>
                    </div>
                    <Button onClick={() => { setEditingCircuit(null); setNewCircuit({ name: '', description: '' }); setShowCircuitModal(true); }} className="bg-orange-600 hover:bg-orange-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Circuit
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {loadingCircuits ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
                      </div>
                    ) : circuits.length === 0 ? (
                      <div className="text-center py-8">
                        <Network className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-gray-500">No circuits created yet</p>
                        <p className="text-sm text-gray-400">Click "Add Circuit" to create your first circuit</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {circuits.map((circuit) => (
                          <div key={circuit.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <MapPin className="h-5 w-5 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium">{circuit.name}</p>
                                <p className="text-sm text-gray-500">{circuit.description || 'No description'}</p>
                                {circuit.siso_name && (
                                  <p className="text-xs text-green-600 mt-1">
                                    <CheckCircle className="h-3 w-3 inline mr-1" />
                                    SISO: {circuit.siso_name}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">
                                {circuit.schools_count || 0} Schools
                              </Badge>
                              <Button variant="ghost" size="sm" onClick={() => { setEditingCircuit(circuit); setNewCircuit({ name: circuit.name, description: circuit.description || '' }); setShowCircuitModal(true); }}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteCircuit(circuit.id)} className="text-red-600 hover:text-red-700">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Connected SISOs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-orange-600" />
                      Connected SISOs
                    </CardTitle>
                    <CardDescription>
                      SISOs who have connected to this directorate using the connect token
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingSisos ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
                      </div>
                    ) : connectedSisos.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-gray-500">No SISOs connected yet</p>
                        <p className="text-sm text-gray-400">Share your connect token with SISOs to connect them</p>
                      </div>
                    ) : (
                      <div className="grid gap-3 md:grid-cols-2">
                        {connectedSisos.map((siso) => (
                          <div key={siso.id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{siso.name}</p>
                              <p className="text-sm text-gray-500">{siso.circuit_name || 'No circuit assigned'}</p>
                              <p className="text-xs text-gray-400">{siso.email}</p>
                            </div>
                            <Badge className="bg-green-100 text-green-700">Connected</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Director Applications Tab */}
          {user?.role === 'director' && (
            <TabsContent value="director-applications">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-orange-600" />
                        Applications
                        {directorUnreadCount > 0 && (
                          <Badge className="bg-red-500 text-white">{directorUnreadCount} new</Badge>
                        )}
                      </CardTitle>
                      <CardDescription>
                        View all applications escalated to the directorate
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={directorAppFilter} onValueChange={setDirectorAppFilter}>
                        <SelectTrigger className="w-40">
                          <Filter className="h-4 w-4 mr-2" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Applications</SelectItem>
                          <SelectItem value="filed">Filed to Director</SelectItem>
                          <SelectItem value="escalated">All Escalated</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" onClick={loadDirectorApplications} disabled={loadingDirectorApps}>
                        <RefreshCw className={`h-4 w-4 ${loadingDirectorApps ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingDirectorApps ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
                    </div>
                  ) : directorApplications.length === 0 ? (
                    <div className="text-center py-12">
                      <ClipboardList className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500">No applications found</p>
                      <p className="text-sm text-gray-400">Applications will appear here when filed or escalated</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {directorApplications.map((app) => (
                        <div key={app.id} className={`p-3 sm:p-4 rounded-lg border ${!app.director_read ? 'bg-orange-50 border-orange-200' : 'bg-white'}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge variant="outline" className={app.source === 'teacher' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}>
                                  {app.source === 'teacher' ? 'Teacher' : 'Headteacher'}
                                </Badge>
                                <Badge className={
                                  app.status === 'filed_to_director' ? 'bg-green-100 text-green-700' :
                                  app.status === 'escalated_to_secretary' ? 'bg-amber-100 text-amber-700' :
                                  'bg-gray-100 text-gray-700'
                                }>
                                  {app.status?.replace(/_/g, ' ')}
                                </Badge>
                                {app.director_received && <Badge className="bg-blue-100 text-blue-700">Received</Badge>}
                                {!app.director_read && <span className="w-2 h-2 bg-red-500 rounded-full shrink-0"></span>}
                              </div>
                              <h4 className="font-semibold text-sm sm:text-base truncate">{app.application_type?.replace(/_/g, ' ').toUpperCase()}</h4>
                              <p className="text-sm text-gray-600">Applicant: {app.applicant_name || 'N/A'}</p>
                              <p className="text-sm text-gray-500 truncate">School: {app.school || app.applicant_school || app.school_name || 'N/A'}</p>
                              {app.registration_number && (
                                <p className="text-sm text-blue-600">Reg: {app.registration_number}</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {app.filed_at ? `Filed: ${new Date(app.filed_at).toLocaleDateString()}` : 
                                 app.escalation_date ? `Escalated: ${new Date(app.escalation_date).toLocaleDateString()}` : ''}
                              </p>
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                              <Button variant="outline" size="sm" onClick={() => viewApplication(app)} data-testid={`view-app-${app.id}`}>
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              {!app.director_received && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                  data-testid={`received-btn-${app.id}`}
                                  onClick={async () => {
                                    try {
                                      const res = await authenticatedFetch(`${API_BASE}/api/director/application/${app.id}/received`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ source: app.source })
                                      });
                                      if (res.ok) {
                                        toast.success('Marked as received — notifications sent to applicant chain');
                                        setDirectorApplications(prev => prev.map(a =>
                                          a.id === app.id ? { ...a, director_received: true } : a
                                        ));
                                      } else {
                                        const err = await res.json();
                                        throw new Error(err.detail || 'Failed');
                                      }
                                    } catch (e) { toast.error(e.message); }
                                  }}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Received
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Reports Tab (Director Only) */}
          {user?.role === 'director' && (
            <TabsContent value="reports">
              <div className="space-y-6">
                {/* Report Type Selector */}
                <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow-sm">
                  <Button variant={reportType === 'overview' ? 'default' : 'ghost'} size="sm" onClick={() => setReportType('overview')}>
                    <PieChart className="h-4 w-4 mr-2" />
                    Overview
                  </Button>
                  <Button variant={reportType === 'siso' ? 'default' : 'ghost'} size="sm" onClick={() => setReportType('siso')}>
                    <Users className="h-4 w-4 mr-2" />
                    SISO Performance
                  </Button>
                  <Button variant={reportType === 'applications' ? 'default' : 'ghost'} size="sm" onClick={() => setReportType('applications')}>
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Application Stats
                  </Button>
                  <Button variant={reportType === 'schools' ? 'default' : 'ghost'} size="sm" onClick={() => setReportType('schools')}>
                    <School className="h-4 w-4 mr-2" />
                    Schools Activity
                  </Button>
                </div>

                {loadingDdReports ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
                  </div>
                ) : !reportsData ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500">Select a report type to view data</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Overview Report */}
                    {reportType === 'overview' && (
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                          <CardContent className="p-4">
                            <p className="text-blue-100 text-sm">Total Applications</p>
                            <p className="text-3xl font-bold">{reportsData.total_applications || 0}</p>
                            <TrendingUp className="h-8 w-8 text-blue-200 mt-2" />
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                          <CardContent className="p-4">
                            <p className="text-green-100 text-sm">Approved</p>
                            <p className="text-3xl font-bold">{reportsData.approved_count || 0}</p>
                            <CheckCircle className="h-8 w-8 text-green-200 mt-2" />
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                          <CardContent className="p-4">
                            <p className="text-amber-100 text-sm">Pending</p>
                            <p className="text-3xl font-bold">{reportsData.pending_count || 0}</p>
                            <Clock className="h-8 w-8 text-amber-200 mt-2" />
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                          <CardContent className="p-4">
                            <p className="text-purple-100 text-sm">Active SISOs</p>
                            <p className="text-3xl font-bold">{reportsData.active_sisos || 0}</p>
                            <Users className="h-8 w-8 text-purple-200 mt-2" />
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* SISO Performance Report */}
                    {reportType === 'siso' && (
                      <Card>
                        <CardHeader>
                          <CardTitle>SISO Performance Report</CardTitle>
                          <CardDescription>Applications processed by each SISO</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {reportsData.siso_stats?.length > 0 ? (
                            <div className="space-y-3">
                              {reportsData.siso_stats.map((siso, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                      <User className="h-5 w-5 text-purple-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium">{siso.name}</p>
                                      <p className="text-sm text-gray-500">{siso.circuit_name || 'No circuit'}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm">
                                    <div className="text-center">
                                      <p className="font-bold text-green-600">{siso.approved || 0}</p>
                                      <p className="text-gray-500">Approved</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="font-bold text-red-600">{siso.rejected || 0}</p>
                                      <p className="text-gray-500">Rejected</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="font-bold text-amber-600">{siso.pending || 0}</p>
                                      <p className="text-gray-500">Pending</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center py-8 text-gray-500">No SISO data available</p>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Applications Statistics */}
                    {reportType === 'applications' && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Application Statistics</CardTitle>
                          <CardDescription>Breakdown by application type and status</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {reportsData.application_stats?.length > 0 ? (
                            <div className="space-y-4">
                              {reportsData.application_stats.map((stat, idx) => (
                                <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="font-medium">{stat.type?.replace(/_/g, ' ').toUpperCase()}</p>
                                    <Badge>{stat.total} total</Badge>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="flex-1 bg-green-200 rounded-full h-2">
                                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(stat.approved / stat.total * 100) || 0}%` }}></div>
                                    </div>
                                  </div>
                                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>Approved: {stat.approved || 0}</span>
                                    <span>Pending: {stat.pending || 0}</span>
                                    <span>Rejected: {stat.rejected || 0}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center py-8 text-gray-500">No application statistics available</p>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Schools Activity */}
                    {reportType === 'schools' && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Schools Activity Report</CardTitle>
                          <CardDescription>Teacher application activity by school</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {reportsData.school_stats?.length > 0 ? (
                            <div className="space-y-3">
                              {reportsData.school_stats.map((school, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                      <School className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium">{school.name}</p>
                                      <p className="text-sm text-gray-500">{school.circuit || 'Unknown circuit'}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <Badge variant="outline">{school.teacher_count || 0} Teachers</Badge>
                                    <Badge>{school.application_count || 0} Applications</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center py-8 text-gray-500">No school data available</p>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </TabsContent>
          )}

          {/* User Management Tab (Director Only) */}
          {user?.role === 'director' && (
            <TabsContent value="users">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-orange-600" />
                      Sub-Directorate Officers
                    </CardTitle>
                    <CardDescription>
                      Manage sub-officer accounts and permissions
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => setShowCreateModal(true)}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Sub-Officer
                  </Button>
                </CardHeader>
                <CardContent>
                  {dashboardData?.sub_officers?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.sub_officers.map((officer) => (
                        <div 
                          key={officer.id} 
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            officer.is_active ? 'bg-white' : 'bg-gray-100 opacity-75'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              officer.role === 'dd_supervision' ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                              {officer.role === 'dd_supervision' ? (
                                <Shield className="h-5 w-5 text-purple-600" />
                              ) : (
                                <FileText className="h-5 w-5 text-blue-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{officer.name}</p>
                              <p className="text-sm text-gray-500">{officer.email}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <Badge className={getRoleBadgeColor(officer.role)}>
                              {getRoleDisplayName(officer.role)}
                            </Badge>
                            <Badge className={officer.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {officer.is_active ? 'Active' : 'Disabled'}
                            </Badge>
                            {officer.force_password_reset && (
                              <Badge className="bg-yellow-100 text-yellow-700">
                                Password Reset Required
                              </Badge>
                            )}
                            
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingOfficer({
                                    ...officer,
                                    permissions: officer.assigned_permissions || rolePermissions[officer.role]?.map(p => p.id) || []
                                  });
                                  setShowEditModal(true);
                                }}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => toggleUserStatus(officer.id)}
                                disabled={processingAction}
                              >
                                {officer.is_active ? (
                                  <><UserX className="h-4 w-4 mr-1" />Disable</>
                                ) : (
                                  <><UserCheck className="h-4 w-4 mr-1" />Enable</>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(officer);
                                  setShowResetPasswordModal(true);
                                }}
                              >
                                <Key className="h-4 w-4 mr-1" />
                                Reset
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteSubOfficer(officer.id, officer.name)}
                                disabled={processingAction}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No sub-officers created yet</p>
                      <p className="text-sm text-gray-400 mt-2">
                        Click "Add Sub-Officer" to create sub-officer accounts
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Role Permissions Info */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-orange-600" />
                    Role-Based Access Control
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Landmark className="h-5 w-5 text-orange-600" />
                        <h4 className="font-semibold text-orange-900">Director</h4>
                      </div>
                      <ul className="text-sm text-orange-800 space-y-1">
                        <li>• Full system visibility</li>
                        <li>• Final approval authority</li>
                        <li>• Financial oversight</li>
                        <li>• User management</li>
                      </ul>
                    </div>
                    
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-5 w-5 text-purple-600" />
                        <h4 className="font-semibold text-purple-900">Head Of Supervision</h4>
                      </div>
                      <ul className="text-sm text-purple-800 space-y-1">
                        <li>• Supervisory oversight</li>
                        <li>• Monitor circuits/SISOs</li>
                        <li>• View delays & bottlenecks</li>
                      </ul>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-5 w-5 text-blue-600" />
                        <h4 className="font-semibold text-blue-900">Secretary</h4>
                      </div>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Registry functions</li>
                        <li>• Document management</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="h-5 w-5 text-teal-600" />
                        <h4 className="font-semibold text-teal-900">HRM</h4>
                      </div>
                      <ul className="text-sm text-teal-800 space-y-1">
                        <li>• Human resource management</li>
                        <li>• Staff records & welfare</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-5 w-5 text-cyan-600" />
                        <h4 className="font-semibold text-cyan-900">Personnel</h4>
                      </div>
                      <ul className="text-sm text-cyan-800 space-y-1">
                        <li>• Personnel administration</li>
                        <li>• Staff documentation</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-5 w-5 text-indigo-600" />
                        <h4 className="font-semibold text-indigo-900">Supervision Team</h4>
                      </div>
                      <ul className="text-sm text-indigo-800 space-y-1">
                        <li>• Field supervision support</li>
                        <li>• School monitoring visits</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* SISO Connections Tab (DD-Supervision Only) - View Only */}
          {user?.role === 'dd_supervision' && (
            <TabsContent value="sisos">
              {/* Connected SISOs List */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-600" />
                      Connected SISOs
                    </CardTitle>
                    <CardDescription>
                      SISOs connected to your district - Click to expand and view circuit details
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={loadConnectedSisos} disabled={loadingSisos}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingSisos ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingSisos ? (
                    <div className="text-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-600" />
                      <p className="text-gray-500 mt-2">Loading connected SISOs...</p>
                    </div>
                  ) : connectedSisos.length > 0 ? (
                    <div className="space-y-3">
                      {connectedSisos.map((siso) => (
                        <div key={siso.id} className="border rounded-lg overflow-hidden">
                          {/* SISO Card Header - Clickable */}
                          <div 
                            className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                              expandedSisoId === siso.id ? 'bg-purple-50 border-b border-purple-200' : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() => toggleSisoExpand(siso.id)}
                            data-testid={`siso-card-${siso.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex items-center">
                                {expandedSisoId === siso.id ? (
                                  <ChevronDown className="h-5 w-5 text-purple-600 mr-2" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-gray-400 mr-2" />
                                )}
                                {siso.profile_picture ? (
                                  <img 
                                    src={`${API_BASE}${siso.profile_picture}`} 
                                    alt={siso.name}
                                    className="w-10 h-10 rounded-full object-cover border-2 border-green-200"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <Users className="h-5 w-5 text-green-600" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{siso.name}</p>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Mail className="h-3 w-3" />
                                  <span>{siso.email}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-sm font-medium text-purple-700">Circuit: {siso.circuit_name || 'N/A'}</p>
                                <p className="text-xs text-gray-500">{siso.school_count || 0} schools</p>
                              </div>
                              <Badge className="bg-green-100 text-green-700">
                                Connected
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Expanded Content - Circuit Details */}
                          {expandedSisoId === siso.id && (
                            <div className="bg-white p-4 border-t">
                              {loadingCircuitData[siso.id] ? (
                                <div className="text-center py-8">
                                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-600" />
                                  <p className="text-sm text-gray-500 mt-2">Loading circuit data...</p>
                                </div>
                              ) : sisoCircuitData[siso.id] ? (
                                <div className="space-y-4">
                                  {/* SISO Profile Summary */}
                                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                                      <User className="h-4 w-4" />
                                      SISO Profile
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                      <div>
                                        <p className="text-gray-500">Circuit</p>
                                        <p className="font-medium">{sisoCircuitData[siso.id].siso.circuit_name || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">District</p>
                                        <p className="font-medium">{sisoCircuitData[siso.id].siso.district_name || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Phone</p>
                                        <p className="font-medium">{sisoCircuitData[siso.id].siso.phone || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Town</p>
                                        <p className="font-medium">{sisoCircuitData[siso.id].siso.town || 'N/A'}</p>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Stats Row */}
                                  <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                                      <School className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                                      <p className="text-2xl font-bold text-blue-700">{sisoCircuitData[siso.id].total_schools}</p>
                                      <p className="text-xs text-blue-600">Schools</p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                                      <GraduationCap className="h-5 w-5 mx-auto text-green-600 mb-1" />
                                      <p className="text-2xl font-bold text-green-700">{sisoCircuitData[siso.id].total_headteachers}</p>
                                      <p className="text-xs text-green-600">Headteachers</p>
                                    </div>
                                    <div className="bg-orange-50 rounded-lg p-3 text-center border border-orange-200">
                                      <BookOpen className="h-5 w-5 mx-auto text-orange-600 mb-1" />
                                      <p className="text-2xl font-bold text-orange-700">{sisoCircuitData[siso.id].total_teachers}</p>
                                      <p className="text-xs text-orange-600">Teachers</p>
                                    </div>
                                  </div>

                                  {/* Schools List */}
                                  <div>
                                    <div className="flex items-center justify-between mb-3">
                                      <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                                        <School className="h-4 w-4 text-blue-600" />
                                        Schools in Circuit
                                      </h4>
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={(e) => { e.stopPropagation(); refreshCircuitData(siso.id); }}
                                        disabled={loadingCircuitData[siso.id]}
                                      >
                                        <RefreshCw className={`h-3 w-3 mr-1 ${loadingCircuitData[siso.id] ? 'animate-spin' : ''}`} />
                                        Refresh
                                      </Button>
                                    </div>
                                    
                                    {sisoCircuitData[siso.id].schools.length > 0 ? (
                                      <div className="space-y-3">
                                        {sisoCircuitData[siso.id].schools.map((school, idx) => (
                                          <div key={school.id || idx} className="border rounded-lg overflow-hidden">
                                            {/* School Header - Clickable to expand */}
                                            <div 
                                              className={`p-3 cursor-pointer transition-colors ${
                                                expandedSchoolId === school.id 
                                                  ? 'bg-blue-100 border-b border-blue-300' 
                                                  : 'bg-blue-50 hover:bg-blue-100'
                                              }`}
                                              onClick={(e) => { e.stopPropagation(); toggleSchoolExpand(school.id); }}
                                            >
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                  {expandedSchoolId === school.id ? (
                                                    <ChevronDown className="h-4 w-4 text-blue-600" />
                                                  ) : (
                                                    <ChevronRight className="h-4 w-4 text-blue-400" />
                                                  )}
                                                  <School className="h-4 w-4 text-blue-600" />
                                                  <span className="font-medium text-blue-800">{school.school_name}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  {school.headteacher && (
                                                    <Badge className="bg-green-100 text-green-700 text-xs">
                                                      1 Headteacher
                                                    </Badge>
                                                  )}
                                                  <Badge variant="outline" className="text-xs">
                                                    {school.teachers?.length || 0} teachers
                                                  </Badge>
                                                </div>
                                              </div>
                                            </div>
                                            
                                            {/* Expanded School Content */}
                                            {expandedSchoolId === school.id && (
                                              <div className="bg-white">
                                                {/* Quick Stats Banner */}
                                                {school.stats && (
                                                  <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                                                    <div className="flex items-center justify-between">
                                                      <div className="flex items-center gap-4">
                                                        <div className="flex items-center gap-2">
                                                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                            <Users className="h-4 w-4 text-blue-600" />
                                                          </div>
                                                          <div>
                                                            <p className="text-xs text-gray-500">Teachers</p>
                                                            <p className="text-sm font-bold text-blue-700">{school.stats.total_teachers}</p>
                                                          </div>
                                                        </div>
                                                        <div className="h-8 w-px bg-gray-200" />
                                                        <div className="flex items-center gap-2">
                                                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                                            <FileText className="h-4 w-4 text-purple-600" />
                                                          </div>
                                                          <div>
                                                            <p className="text-xs text-gray-500">Submissions</p>
                                                            <p className="text-sm font-bold text-purple-700">{school.stats.total_submissions}</p>
                                                          </div>
                                                        </div>
                                                        <div className="h-8 w-px bg-gray-200" />
                                                        <div className="flex items-center gap-2">
                                                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                                          </div>
                                                          <div>
                                                            <p className="text-xs text-gray-500">Vetted</p>
                                                            <p className="text-sm font-bold text-green-700">{school.stats.vetted_submissions}</p>
                                                          </div>
                                                        </div>
                                                        <div className="h-8 w-px bg-gray-200" />
                                                        <div className="flex items-center gap-2">
                                                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${school.stats.pending_vetting > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                                                            <Clock className={`h-4 w-4 ${school.stats.pending_vetting > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
                                                          </div>
                                                          <div>
                                                            <p className="text-xs text-gray-500">Pending</p>
                                                            <p className={`text-sm font-bold ${school.stats.pending_vetting > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                                                              {school.stats.pending_vetting}
                                                            </p>
                                                          </div>
                                                        </div>
                                                      </div>
                                                      {school.stats.total_submissions > 0 && (
                                                        <div className="text-right">
                                                          <p className="text-xs text-gray-500">Vetting Rate</p>
                                                          <p className={`text-lg font-bold ${
                                                            (school.stats.vetted_submissions / school.stats.total_submissions) >= 0.8 ? 'text-green-600' :
                                                            (school.stats.vetted_submissions / school.stats.total_submissions) >= 0.5 ? 'text-amber-600' : 'text-red-600'
                                                          }`}>
                                                            {Math.round((school.stats.vetted_submissions / school.stats.total_submissions) * 100)}%
                                                          </p>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                                
                                                {/* Headteacher Section */}
                                                <div className="p-4 border-b">
                                                  <p className="text-xs font-medium text-gray-500 mb-3 flex items-center gap-1 uppercase tracking-wide">
                                                    <GraduationCap className="h-3 w-3" />
                                                    Headteacher
                                                  </p>
                                                  {school.headteacher ? (
                                                    <div 
                                                      className="flex items-center gap-4 p-3 bg-green-50 rounded-lg border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
                                                      onClick={(e) => { e.stopPropagation(); viewProfile(school.headteacher, 'headteacher'); }}
                                                    >
                                                      {school.headteacher.profile_picture ? (
                                                        <img 
                                                          src={`${API_BASE}${school.headteacher.profile_picture}`} 
                                                          alt={school.headteacher.name}
                                                          className="w-12 h-12 rounded-full object-cover border-2 border-green-300"
                                                        />
                                                      ) : (
                                                        <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                                                          <User className="h-6 w-6 text-green-600" />
                                                        </div>
                                                      )}
                                                      <div className="flex-1">
                                                        <p className="font-semibold text-green-800">{school.headteacher.name}</p>
                                                        <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                                                          <span className="flex items-center gap-1">
                                                            <Mail className="h-3 w-3" />
                                                            {school.headteacher.email}
                                                          </span>
                                                          {school.headteacher.phone && (
                                                            <span className="flex items-center gap-1">
                                                              <Phone className="h-3 w-3" />
                                                              {school.headteacher.phone}
                                                            </span>
                                                          )}
                                                        </div>
                                                        {school.headteacher.file_number && (
                                                          <p className="text-xs text-green-600 mt-1">File #: {school.headteacher.file_number}</p>
                                                        )}
                                                      </div>
                                                      <div className="text-green-500">
                                                        <Eye className="h-4 w-4" />
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                                                      <User className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                                                      <p className="text-sm text-gray-400 italic">No headteacher connected</p>
                                                    </div>
                                                  )}
                                                </div>
                                                
                                                {/* Teachers Section */}
                                                <div className="p-4">
                                                  <p className="text-xs font-medium text-gray-500 mb-3 flex items-center gap-1 uppercase tracking-wide">
                                                    <BookOpen className="h-3 w-3" />
                                                    Teachers ({school.teachers?.length || 0})
                                                  </p>
                                                  {school.teachers && school.teachers.length > 0 ? (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                      {school.teachers.map((teacher, tIdx) => (
                                                        <div 
                                                          key={teacher.id || tIdx} 
                                                          className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors"
                                                          onClick={(e) => { e.stopPropagation(); viewProfile(teacher, 'teacher'); }}
                                                        >
                                                          {teacher.profile_picture ? (
                                                            <img 
                                                              src={`${API_BASE}${teacher.profile_picture}`} 
                                                              alt={teacher.name}
                                                              className="w-10 h-10 rounded-full object-cover border-2 border-orange-300"
                                                            />
                                                          ) : (
                                                            <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center">
                                                              <User className="h-5 w-5 text-orange-600" />
                                                            </div>
                                                          )}
                                                          <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-sm text-orange-800 truncate">{teacher.name}</p>
                                                            <p className="text-xs text-gray-500 truncate">{teacher.assigned_class || 'No class assigned'}</p>
                                                            {/* Teacher Stats Badges */}
                                                            {teacher.stats && (
                                                              <div className="flex items-center gap-1.5 mt-1">
                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
                                                                  {teacher.stats.total_submissions} notes
                                                                </span>
                                                                {teacher.stats.pending > 0 && (
                                                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                                                                    {teacher.stats.pending} pending
                                                                  </span>
                                                                )}
                                                              </div>
                                                            )}
                                                          </div>
                                                          <div className="text-orange-500">
                                                            <Eye className="h-4 w-4" />
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  ) : (
                                                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                                                      <Users className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                                                      <p className="text-sm text-gray-400 italic">No teachers in this school</p>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-center py-6 bg-gray-50 rounded-lg">
                                        <School className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                                        <p className="text-sm text-gray-500">No schools in this circuit yet</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-8 text-gray-500">
                                  <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                                  <p className="text-sm">Failed to load circuit data</p>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="mt-2"
                                    onClick={() => toggleSisoExpand(siso.id)}
                                  >
                                    Try Again
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Network className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No SISOs connected yet</p>
                      <p className="text-sm text-gray-400 mt-2">
                        Share your connect token with SISOs to get them connected
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Reports Tab (DD-Supervision Only) */}
          {user?.role === 'dd_supervision' && (
            <TabsContent value="reports">
              <div className="space-y-6">
                {/* Header with Refresh */}
                <Card className="border-blue-200">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-blue-800">
                          <BarChart3 className="h-6 w-6" />
                          Reports & Analytics
                        </CardTitle>
                        <CardDescription className="text-blue-600">
                          District performance reports and SISO statistics for {ddReportsData?.district_name || 'your district'}
                        </CardDescription>
                      </div>
                      <Button variant="outline" onClick={loadDdReportsData} disabled={loadingDdReports}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loadingDdReports ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                </Card>

                {loadingDdReports ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    <span className="ml-2 text-gray-500">Loading reports...</span>
                  </div>
                ) : ddReportsData ? (
                  <>
                    {/* District Stats Section */}
                    <Card className="border-purple-200">
                      <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 pb-3">
                        <CardTitle className="flex items-center gap-2 text-purple-800 text-lg">
                          <PieChart className="h-5 w-5" />
                          District Statistics
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-5 gap-3">
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg text-center border border-blue-200">
                            <p className="text-3xl font-bold text-blue-700">{ddReportsData.district_stats?.circuits || 0}</p>
                            <p className="text-sm text-blue-600 mt-1">Circuits</p>
                          </div>
                          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg text-center border border-purple-200">
                            <p className="text-3xl font-bold text-purple-700">{ddReportsData.district_stats?.sisos || 0}</p>
                            <p className="text-sm text-purple-600 mt-1">SISOs</p>
                          </div>
                          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg text-center border border-orange-200">
                            <p className="text-3xl font-bold text-orange-700">{ddReportsData.district_stats?.schools || 0}</p>
                            <p className="text-sm text-orange-600 mt-1">Schools</p>
                          </div>
                          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg text-center border border-green-200">
                            <p className="text-3xl font-bold text-green-700">{ddReportsData.district_stats?.headteachers || 0}</p>
                            <p className="text-sm text-green-600 mt-1">Headteachers</p>
                          </div>
                          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg text-center border border-indigo-200">
                            <p className="text-3xl font-bold text-indigo-700">{ddReportsData.district_stats?.teachers || 0}</p>
                            <p className="text-sm text-indigo-600 mt-1">Teachers</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Performance Trends Section */}
                    <Card className="border-green-200">
                      <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 pb-3">
                        <CardTitle className="flex items-center gap-2 text-green-800 text-lg">
                          <TrendingUp className="h-5 w-5" />
                          Performance Trends by Circuit
                        </CardTitle>
                        <CardDescription className="text-green-600">
                          Teacher lesson note submission and vetting rates per circuit
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {ddReportsData.performance_trends?.length > 0 ? (
                          <div className="space-y-3">
                            {ddReportsData.performance_trends.map((circuit, index) => (
                              <div key={circuit.circuit} className="border rounded-lg p-4 bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                                      index === 0 ? 'bg-green-500' : index === 1 ? 'bg-blue-500' : 'bg-gray-400'
                                    }`}>
                                      {index + 1}
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-800">{circuit.circuit} Circuit</h4>
                                      <p className="text-xs text-gray-500">
                                        {circuit.siso_count} SISO(s) • {circuit.school_count} School(s) • {circuit.teacher_count} Teacher(s)
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className={`text-2xl font-bold ${
                                      circuit.lesson_notes.vetting_rate >= 80 ? 'text-green-600' :
                                      circuit.lesson_notes.vetting_rate >= 50 ? 'text-amber-600' : 'text-red-600'
                                    }`}>
                                      {circuit.lesson_notes.vetting_rate}%
                                    </p>
                                    <p className="text-xs text-gray-500">Vetting Rate</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                                  <div className="bg-white p-2 rounded border">
                                    <p className="font-bold text-blue-600">{circuit.lesson_notes.submitted}</p>
                                    <p className="text-xs text-gray-500">Submitted</p>
                                  </div>
                                  <div className="bg-white p-2 rounded border">
                                    <p className="font-bold text-green-600">{circuit.lesson_notes.vetted}</p>
                                    <p className="text-xs text-gray-500">Vetted</p>
                                  </div>
                                  <div className="bg-white p-2 rounded border">
                                    <p className="font-bold text-amber-600">{circuit.lesson_notes.submitted - circuit.lesson_notes.vetted}</p>
                                    <p className="text-xs text-gray-500">Pending</p>
                                  </div>
                                </div>
                                {/* Progress Bar */}
                                <div className="mt-3 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      circuit.lesson_notes.vetting_rate >= 80 ? 'bg-green-500' :
                                      circuit.lesson_notes.vetting_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${circuit.lesson_notes.vetting_rate}%` }}
                                  ></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <TrendingUp className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                            <p>No circuit performance data available yet</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* SISO Analytics Section */}
                    <Card className="border-orange-200">
                      <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 pb-3">
                        <CardTitle className="flex items-center gap-2 text-orange-800 text-lg">
                          <Users className="h-5 w-5" />
                          SISO Support Action Analytics
                        </CardTitle>
                        <CardDescription className="text-orange-600">
                          Analysis of support actions given to teachers
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-4">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-3 gap-3 mb-6">
                          <div className="bg-blue-50 p-4 rounded-lg text-center border border-blue-200">
                            <p className="text-3xl font-bold text-blue-700">{ddReportsData.siso_analytics?.total_support_actions || 0}</p>
                            <p className="text-sm text-blue-600">Total Actions</p>
                          </div>
                          <div className="bg-amber-50 p-4 rounded-lg text-center border border-amber-200">
                            <p className="text-3xl font-bold text-amber-700">{ddReportsData.siso_analytics?.active_actions || 0}</p>
                            <p className="text-sm text-amber-600">Active</p>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg text-center border border-green-200">
                            <p className="text-3xl font-bold text-green-700">{ddReportsData.siso_analytics?.completed_actions || 0}</p>
                            <p className="text-sm text-green-600">Completed</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* By Action Type */}
                          <div className="border rounded-lg p-4">
                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <Target className="h-4 w-4" />
                              By Support Area
                            </h4>
                            {ddReportsData.siso_analytics?.by_action_type?.length > 0 ? (
                              <div className="space-y-2">
                                {ddReportsData.siso_analytics.by_action_type.map(type => (
                                  <div key={type.action_type} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                    <span className="text-sm text-gray-700">{type.label}</span>
                                    <div className="flex items-center gap-2">
                                      <Badge className="bg-blue-100 text-blue-700">{type.total}</Badge>
                                      {type.completed > 0 && (
                                        <Badge className="bg-green-100 text-green-700">{type.completed} done</Badge>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm text-center py-4">No support actions yet</p>
                            )}
                          </div>

                          {/* By SISO */}
                          <div className="border rounded-lg p-4">
                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              Actions by SISO
                            </h4>
                            {ddReportsData.siso_analytics?.by_siso?.length > 0 ? (
                              <div className="space-y-2">
                                {ddReportsData.siso_analytics.by_siso.map(siso => (
                                  <div key={siso.siso_id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                    <span className="text-sm text-gray-700 font-medium">{siso.name}</span>
                                    <div className="flex items-center gap-1">
                                      <Badge className="bg-blue-100 text-blue-700">{siso.total}</Badge>
                                      {siso.active > 0 && (
                                        <Badge className="bg-amber-100 text-amber-700">{siso.active} active</Badge>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm text-center py-4">No SISOs have created actions</p>
                            )}
                          </div>
                        </div>

                        {/* Recent Actions */}
                        {ddReportsData.siso_analytics?.recent_actions?.length > 0 && (
                          <div className="mt-4 border rounded-lg p-4">
                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              Recent Support Actions
                            </h4>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {ddReportsData.siso_analytics.recent_actions.map(action => (
                                <div key={action.id} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium truncate">{action.action_title}</p>
                                    <p className="text-xs text-gray-500">
                                      {action.siso_name} → {action.teacher_name} • {new Date(action.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <Badge className={action.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                    {action.status}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center py-8 text-gray-500">
                        <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                        <p>Click "Refresh" to load reports data</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {/* Audit Logs Tab (Director Only) */}
          {user?.role === 'director' && (
            <TabsContent value="audit">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-orange-600" />
                      Audit Logs
                    </CardTitle>
                    <CardDescription>
                      Track all user management activities
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={loadAuditLogs} disabled={loadingLogs}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingLogs ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {loadingLogs ? (
                    <div className="text-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-orange-600" />
                      <p className="text-gray-500 mt-2">Loading logs...</p>
                    </div>
                  ) : auditLogs.length > 0 ? (
                    <div className="space-y-3">
                      {auditLogs.map((log) => (
                        <div key={log.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border">
                          {getActionIcon(log.action)}
                          <div className="flex-1">
                            <p className="font-medium">{log.details}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <span>{log.target_user_name}</span>
                              <span>•</span>
                              <span>{log.target_user_email}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {new Date(log.timestamp).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No audit logs yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Secretary Applications Tab */}
          {user?.role === 'secretary' && (
            <TabsContent value="applications">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      Escalated Applications
                      {unreadAppCount > 0 && (
                        <Badge className="bg-red-500 text-white">{unreadAppCount} new</Badge>
                      )}
                    </CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={loadSecretaryApplications}
                      disabled={loadingApplications}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${loadingApplications ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                  <CardDescription>
                    Applications escalated from SISOs for registration and filing
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingApplications ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                  ) : secretaryApplications.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500">No escalated applications</p>
                      <p className="text-sm text-gray-400">Applications from SISOs will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {secretaryApplications.map((app) => (
                        <div
                          key={app.id}
                          className={`p-4 rounded-lg border ${!app.secretary_read ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'} hover:shadow-md transition-shadow`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className={`${
                                  app.source === 'teacher' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {app.source === 'teacher' ? 'Teacher' : 'Headteacher'}
                                </Badge>
                                <Badge className={`${
                                  app.status === 'escalated_to_secretary' ? 'bg-amber-100 text-amber-700' :
                                  app.status === 'registered_by_secretary' ? 'bg-blue-100 text-blue-700' :
                                  app.status === 'filed_to_director' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {app.status?.replace(/_/g, ' ').replace(/by /g, 'by ').replace(/to /g, 'to ')}
                                </Badge>
                                {!app.secretary_read && (
                                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                )}
                              </div>
                              
                              <h4 className="font-semibold text-gray-900">
                                {app.application_type?.replace(/_/g, ' ').toUpperCase() || 'Unknown Type'}
                              </h4>
                              
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">Applicant:</span> {app.applicant_name || app.name || 'N/A'}
                              </p>
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">School:</span> {app.school || app.applicant_school || 'N/A'}
                              </p>
                              
                              {app.registration_number && (
                                <p className="text-sm text-blue-600 mt-1">
                                  <span className="font-medium">Reg. No:</span> {app.registration_number}
                                </p>
                              )}
                              
                              <p className="text-xs text-gray-400 mt-2">
                                Escalated: {app.escalation_date ? new Date(app.escalation_date).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                            
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewApplication(app)}
                                className="w-full"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              
                              {app.status === 'escalated_to_secretary' && (
                                <Button
                                  size="sm"
                                  onClick={() => openRegisterModal(app)}
                                  className="bg-blue-600 hover:bg-blue-700 w-full"
                                >
                                  <BookOpen className="h-4 w-4 mr-1" />
                                  Register
                                </Button>
                              )}
                              
                              {app.status === 'registered_by_secretary' && (
                                <Button
                                  size="sm"
                                  onClick={() => fileToDirector(app)}
                                  disabled={processingAppAction}
                                  className="bg-green-600 hover:bg-green-700 w-full"
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  File to Director
                                </Button>
                              )}
                              
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => exportApplication(app)}
                                className="w-full"
                              >
                                <Printer className="h-4 w-4 mr-1" />
                                Export/Print
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Wallet Tab - Director Only */}
          {user?.role === 'director' && (
            <TabsContent value="wallet">
              <div className="space-y-6">
                {/* Wallet Balance Card */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-emerald-800">Available Balance</CardTitle>
                      <Wallet className="h-5 w-5 text-emerald-600" />
                    </CardHeader>
                    <CardContent>
                      {loadingWallet ? (
                        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                      ) : (
                        <>
                          <div className="text-3xl font-bold text-emerald-700">
                            GH₵ {walletData?.wallet?.balance?.toLocaleString() || '0.00'}
                          </div>
                          <p className="text-xs text-emerald-600 mt-1">From application handling fees</p>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-blue-800">Term Revenue</CardTitle>
                      <ArrowDownLeft className="h-5 w-5 text-blue-600" />
                    </CardHeader>
                    <CardContent>
                      {loadingWallet ? (
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                      ) : (
                        <>
                          <div className="text-3xl font-bold text-blue-700">
                            GH₵ {(walletData?.stats?.term_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </div>
                          <p className="text-xs text-blue-600 mt-1">
                            {walletData?.active_term ? `${walletData.active_term.name} — ${walletData.active_term.academic_year}` : 'No active term'}
                          </p>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-purple-800">Total Withdrawn</CardTitle>
                      <ArrowUpRight className="h-5 w-5 text-purple-600" />
                    </CardHeader>
                    <CardContent>
                      {loadingWallet ? (
                        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                      ) : (
                        <>
                          <div className="text-3xl font-bold text-purple-700">
                            GH₵ {walletData?.wallet?.total_withdrawn?.toLocaleString() || '0.00'}
                          </div>
                          <p className="text-xs text-purple-600 mt-1">Successfully withdrawn</p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Application Stats & Fee Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* District Applications Stats */}
                  <Card className="border-orange-200">
                    <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
                      <CardTitle className="flex items-center gap-2 text-orange-800">
                        <ClipboardList className="h-5 w-5" />
                        District Application Handling
                      </CardTitle>
                      <CardDescription className="text-orange-600">
                        Paid applications in {walletData?.district_name || 'your district'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {loadingWallet ? (
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-orange-600" />
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="p-3 bg-blue-50 rounded-lg">
                              <p className="text-2xl font-bold text-blue-700">{walletData?.stats?.teacher_applications_paid || 0}</p>
                              <p className="text-xs text-blue-600">Teacher Apps</p>
                            </div>
                            <div className="p-3 bg-green-50 rounded-lg">
                              <p className="text-2xl font-bold text-green-700">{walletData?.stats?.headteacher_applications_paid || 0}</p>
                              <p className="text-xs text-green-600">Headteacher Apps</p>
                            </div>
                            <div className="p-3 bg-orange-50 rounded-lg">
                              <p className="text-2xl font-bold text-orange-700">{walletData?.stats?.total_applications_paid || 0}</p>
                              <p className="text-xs text-orange-600">Total Paid</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Fee Breakdown Card */}
                  <Card className="border-indigo-200">
                    <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
                      <CardTitle className="flex items-center gap-2 text-indigo-800">
                        <PieChart className="h-5 w-5" />
                        Fee Breakdown Per Application
                      </CardTitle>
                      <CardDescription className="text-indigo-600">
                        How application fees are distributed
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {loadingWallet ? (
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" />
                      ) : walletData?.fee_breakdown ? (
                        <div className="space-y-3">
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Application Fee (X)</span>
                              <span className="font-semibold">GH₵ {walletData.fee_breakdown.application_fee?.toFixed(2) || '0.00'}</span>
                            </div>
                          </div>
                          <div className="p-3 bg-red-50 rounded-lg">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-red-600">Paystack Fee (2%)</span>
                              <span className="font-semibold text-red-700">- GH₵ {((walletData.fee_breakdown.application_fee || 0) * 0.02).toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="border-t pt-2">
                            <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
                              <span>Remaining (98%)</span>
                              <span>GH₵ {((walletData.fee_breakdown.application_fee || 0) * 0.98).toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="p-3 bg-emerald-100 rounded-lg border-2 border-emerald-300">
                              <p className="text-xs text-emerald-600">Your Share (68%)</p>
                              <p className="text-xl font-bold text-emerald-700">GH₵ {walletData.fee_breakdown.director_share_per_app?.toFixed(2) || '0.00'}</p>
                            </div>
                            <div className="p-3 bg-gray-100 rounded-lg">
                              <p className="text-xs text-gray-500">Admin Share (32%)</p>
                              <p className="text-xl font-bold text-gray-600">GH₵ {((walletData.fee_breakdown.application_fee || 0) * 0.98 * 0.32).toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center">Fee breakdown not available</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Withdrawal Request Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Banknote className="h-5 w-5 text-green-600" />
                        Request Withdrawal
                      </CardTitle>
                      <CardDescription>
                        Withdraw your earnings to mobile money
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Mobile Money Account Details */}
                      <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-700 flex items-center gap-2">
                            <CreditCard className="h-4 w-4" />
                            Mobile Money Account
                          </h4>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setEditingAccount(!editingAccount)}
                          >
                            {editingAccount ? 'Cancel' : 'Edit'}
                          </Button>
                        </div>
                        
                        {editingAccount ? (
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs text-gray-500">Account Name</label>
                              <Input
                                placeholder="Full name on account"
                                value={accountDetails.account_name}
                                onChange={(e) => setAccountDetails({...accountDetails, account_name: e.target.value})}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Network</label>
                              <select 
                                className="w-full border rounded-md px-3 py-2"
                                value={accountDetails.mobile_money_network}
                                onChange={(e) => setAccountDetails({...accountDetails, mobile_money_network: e.target.value})}
                              >
                                <option value="">Select Network</option>
                                <option value="MTN">MTN Mobile Money</option>
                                <option value="Vodafone">Vodafone Cash</option>
                                <option value="AirtelTigo">AirtelTigo Money</option>
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Phone Number</label>
                              <Input
                                placeholder="0XX XXX XXXX"
                                value={accountDetails.mobile_money_number}
                                onChange={(e) => setAccountDetails({...accountDetails, mobile_money_number: e.target.value})}
                              />
                            </div>
                            <Button 
                              onClick={saveAccountDetails} 
                              disabled={savingAccount}
                              className="w-full"
                            >
                              {savingAccount ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : null}
                              Save Account Details
                            </Button>
                          </div>
                        ) : walletData?.wallet?.mobile_money_number ? (
                          <div className="text-sm space-y-1">
                            <p><span className="text-gray-500">Name:</span> {walletData.wallet.account_name}</p>
                            <p><span className="text-gray-500">Network:</span> {walletData.wallet.mobile_money_network}</p>
                            <p><span className="text-gray-500">Number:</span> {walletData.wallet.mobile_money_number}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-amber-600">
                            Please set up your mobile money account to request withdrawals
                          </p>
                        )}
                      </div>

                      {/* Withdrawal Form */}
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium">Withdrawal Amount (GH₵)</label>
                          <Input
                            type="number"
                            placeholder="Enter amount"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            min="1"
                            max={walletData?.wallet?.balance || 0}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Available: GH₵ {walletData?.wallet?.balance?.toLocaleString() || '0.00'}
                          </p>
                        </div>
                        <Button 
                          onClick={requestWithdrawal}
                          disabled={processingWithdrawal || !walletData?.wallet?.mobile_money_number}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          {processingWithdrawal ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4 mr-2" />
                          )}
                          Request Withdrawal
                        </Button>
                      </div>

                      {/* Pending Withdrawals */}
                      {walletData?.pending_withdrawals?.length > 0 && (
                        <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                          <h5 className="text-sm font-medium text-amber-800 mb-2">Pending Withdrawals</h5>
                          {walletData.pending_withdrawals.map((w) => (
                            <div key={w.id} className="flex items-center justify-between text-sm">
                              <span>GH₵ {w.amount?.toLocaleString()}</span>
                              <Badge className="bg-amber-100 text-amber-700">Processing</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Transaction History Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <History className="h-5 w-5 text-blue-600" />
                        Transaction History
                      </CardTitle>
                      <CardDescription>
                        Recent wallet transactions
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingWallet ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                      ) : walletData?.transactions?.length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {walletData.transactions.map((tx) => (
                            <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${
                                  tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                                }`}>
                                  {tx.type === 'credit' ? (
                                    <ArrowDownLeft className={`h-4 w-4 text-green-600`} />
                                  ) : (
                                    <ArrowUpRight className={`h-4 w-4 text-red-600`} />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{tx.description}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {tx.application_source && (
                                      <Badge className={`text-xs ${tx.application_source === 'teacher' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                        {tx.application_source === 'teacher' ? 'Teacher' : 'Headteacher'}
                                      </Badge>
                                    )}
                                    <p className="text-xs text-gray-500">
                                      {new Date(tx.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`font-semibold ${
                                  tx.type === 'credit' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {tx.type === 'credit' ? '+' : '-'}GH₵ {Math.abs(tx.amount).toLocaleString()}
                                </p>
                                <Badge variant="outline" className="text-xs">
                                  {tx.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <History className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>No transactions yet</p>
                          <p className="text-sm">Earnings from application fees will appear here</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Application Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-indigo-600" />
                      Application Handling Stats
                    </CardTitle>
                    <CardDescription>
                      Applications processed that generated support fees
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-3xl font-bold text-blue-700">
                          {walletData?.stats?.teacher_applications_handled || 0}
                        </p>
                        <p className="text-sm text-blue-600">Teacher Applications</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-3xl font-bold text-purple-700">
                          {walletData?.stats?.headteacher_applications_handled || 0}
                        </p>
                        <p className="text-sm text-purple-600">Headteacher Applications</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Lesson Notes Tab */}
          <TabsContent value="lesson_notes">
            <DirectorLessonNotes />
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Sub-Officer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-orange-600" />
                Create Sub-Officer
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Add a new sub-officer account
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={newOfficer.name}
                  onChange={(e) => setNewOfficer({...newOfficer, name: e.target.value})}
                  placeholder="Enter full name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={newOfficer.email}
                  onChange={(e) => setNewOfficer({...newOfficer, email: e.target.value})}
                  placeholder="Enter email address"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={newOfficer.role} 
                  onValueChange={(value) => setNewOfficer({
                    ...newOfficer, 
                    role: value,
                    permissions: []
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dd_supervision">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-purple-600" />
                        Head Of Supervision
                      </div>
                    </SelectItem>
                    <SelectItem value="secretary">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        Secretary / Administrator
                      </div>
                    </SelectItem>
                    <SelectItem value="hrm">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-teal-600" />
                        HRM
                      </div>
                    </SelectItem>
                    <SelectItem value="personnel">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-cyan-600" />
                        Personnel
                      </div>
                    </SelectItem>
                    <SelectItem value="supervision_team">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-indigo-600" />
                        Supervision Team Member
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Permissions Section - Show when role is selected */}
              {newOfficer.role && rolePermissions[newOfficer.role] && (
                <div className="space-y-2">
                  <Label>Assigned Permissions</Label>
                  <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
                    {rolePermissions[newOfficer.role].map((perm) => (
                      <label key={perm.id} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newOfficer.permissions.includes(perm.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewOfficer({
                                ...newOfficer,
                                permissions: [...newOfficer.permissions, perm.id]
                              });
                            } else {
                              setNewOfficer({
                                ...newOfficer,
                                permissions: newOfficer.permissions.filter(p => p !== perm.id)
                              });
                            }
                          }}
                          className="mt-1 h-4 w-4 text-orange-600 rounded border-gray-300"
                        />
                        <div>
                          <p className="font-medium text-sm">{perm.label}</p>
                          <p className="text-xs text-gray-500">{perm.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Temporary Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newOfficer.temp_password}
                    onChange={(e) => setNewOfficer({...newOfficer, temp_password: e.target.value})}
                    placeholder="Create temporary password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  User will be required to change this on first login
                </p>
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateModal(false);
                  setNewOfficer({ name: '', email: '', temp_password: '', role: '', permissions: [] });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={createSubOfficer}
                disabled={processingAction}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {processingAction ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  <><UserPlus className="h-4 w-4 mr-2" />Create Sub-Officer</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Sub-Officer Modal */}
      {showEditModal && editingOfficer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Edit className="h-5 w-5 text-orange-600" />
                Edit Sub-Officer
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Update officer details and permissions
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={editingOfficer.name}
                  onChange={(e) => setEditingOfficer({...editingOfficer, name: e.target.value})}
                  placeholder="Enter full name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editingOfficer.email}
                  onChange={(e) => setEditingOfficer({...editingOfficer, email: e.target.value})}
                  placeholder="Enter email address"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Role</Label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  {editingOfficer.role === 'dd_supervision' ? (
                    <><Shield className="h-5 w-5 text-purple-600" /><span>Head Of Supervision</span></>
                  ) : editingOfficer.role === 'secretary' ? (
                    <><FileText className="h-5 w-5 text-blue-600" /><span>Secretary / Administrator</span></>
                  ) : editingOfficer.role === 'hrm' ? (
                    <><Users className="h-5 w-5 text-teal-600" /><span>HRM</span></>
                  ) : editingOfficer.role === 'personnel' ? (
                    <><User className="h-5 w-5 text-cyan-600" /><span>Personnel</span></>
                  ) : editingOfficer.role === 'supervision_team' ? (
                    <><Target className="h-5 w-5 text-indigo-600" /><span>Supervision Team Member</span></>
                  ) : (
                    <><FileText className="h-5 w-5 text-gray-600" /><span>{editingOfficer.role}</span></>
                  )}
                </div>
                <p className="text-xs text-gray-500">Role cannot be changed. Create a new officer to assign a different role.</p>
              </div>

              {/* Permissions Section */}
              <div className="space-y-2">
                <Label>Assigned Permissions</Label>
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
                  {rolePermissions[editingOfficer.role]?.map((perm) => (
                    <label key={perm.id} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingOfficer.permissions?.includes(perm.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditingOfficer({
                              ...editingOfficer,
                              permissions: [...(editingOfficer.permissions || []), perm.id]
                            });
                          } else {
                            setEditingOfficer({
                              ...editingOfficer,
                              permissions: (editingOfficer.permissions || []).filter(p => p !== perm.id)
                            });
                          }
                        }}
                        className="mt-1 h-4 w-4 text-orange-600 rounded border-gray-300"
                      />
                      <div>
                        <p className="font-medium text-sm">{perm.label}</p>
                        <p className="text-xs text-gray-500">{perm.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingOfficer(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={editSubOfficer}
                disabled={processingAction}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {processingAction ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><CheckCircle className="h-4 w-4 mr-2" />Save Changes</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Circuit Modal */}
      {showCircuitModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Network className="h-5 w-5 text-orange-600" />
                {editingCircuit ? 'Edit Circuit' : 'Add Circuit'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editingCircuit ? 'Update circuit information' : 'Create a new circuit in your district'}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Circuit Name *</Label>
                <Input
                  value={newCircuit.name}
                  onChange={(e) => setNewCircuit({...newCircuit, name: e.target.value})}
                  placeholder="e.g., Circuit A, North Circuit"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Input
                  value={newCircuit.description}
                  onChange={(e) => setNewCircuit({...newCircuit, description: e.target.value})}
                  placeholder="Brief description of the circuit"
                />
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCircuitModal(false);
                  setEditingCircuit(null);
                  setNewCircuit({ name: '', description: '' });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={saveCircuit}
                disabled={processingAction}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {processingAction ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <>{editingCircuit ? 'Update' : 'Create'} Circuit</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Key className="h-5 w-5 text-orange-600" />
                Reset Password
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Reset password for {selectedUser.name}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">User</p>
                <p className="font-medium">{selectedUser.name}</p>
                <p className="text-sm text-gray-500">{selectedUser.email}</p>
              </div>
              
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Minimum 8 characters. User will be required to change on next login.
                </p>
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setSelectedUser(null);
                  setNewPassword('');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={resetUserPassword}
                disabled={processingAction}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {processingAction ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Resetting...</>
                ) : (
                  <><Key className="h-4 w-4 mr-2" />Reset Password</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Profile View Modal */}
      {showProfileModal && selectedProfile && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeProfileModal}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            {/* Profile Header */}
            <div className={`p-6 ${selectedProfile.type === 'headteacher' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-orange-500 to-amber-600'}`}>
              <div className="flex items-center gap-4">
                {selectedProfile.profile_picture ? (
                  <img 
                    src={`${API_BASE}${selectedProfile.profile_picture}`} 
                    alt={selectedProfile.name}
                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center border-4 border-white/50">
                    <User className="h-10 w-10 text-white" />
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white">{selectedProfile.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`${selectedProfile.type === 'headteacher' ? 'bg-green-800' : 'bg-orange-800'} text-white`}>
                      {selectedProfile.type === 'headteacher' ? 'Headteacher' : 'Teacher'}
                    </Badge>
                    {selectedProfile.rank && (
                      <Badge className="bg-white/20 text-white">{selectedProfile.rank}</Badge>
                    )}
                  </div>
                </div>
                <button 
                  onClick={closeProfileModal}
                  className="text-white/80 hover:text-white p-2"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Profile Content */}
            <div className="p-6 space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-sm">{selectedProfile.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-medium text-sm">{selectedProfile.phone || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Portfolio/School Information */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Portfolio in School</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <School className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">School</p>
                      <p className="font-medium text-sm">{selectedProfile.school || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Shield className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">File Number</p>
                      <p className="font-medium text-sm">{selectedProfile.file_number || 'N/A'}</p>
                    </div>
                  </div>
                  {selectedProfile.rank && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <GraduationCap className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Rank</p>
                        <p className="font-medium text-sm">{selectedProfile.rank}</p>
                      </div>
                    </div>
                  )}
                  {selectedProfile.type === 'teacher' && selectedProfile.assigned_class && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <BookOpen className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Assigned Class</p>
                        <p className="font-medium text-sm">{selectedProfile.assigned_class}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Teacher Performance Stats (for DD-Supervision viewing Teachers) */}
              {selectedProfile.type === 'teacher' && user?.role === 'dd_supervision' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Teacher Performance</h3>
                  
                  {loadingTeacherStats ? (
                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Loading stats...</p>
                    </div>
                  ) : teacherStats ? (
                    <div className="space-y-4">
                      {/* Termly Summary Cards */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-600 font-medium">Lesson Notes</p>
                          <p className="text-2xl font-bold text-blue-800">{teacherStats.termly_stats?.lesson_notes?.total_vetted || 0}/{teacherStats.termly_stats?.lesson_notes?.total_submitted || 0}</p>
                          <p className="text-xs text-blue-600">Vetted/Submitted</p>
                          <div className="mt-1 bg-blue-200 rounded-full h-1.5">
                            <div 
                              className="bg-blue-600 h-1.5 rounded-full" 
                              style={{ width: `${teacherStats.termly_stats?.lesson_notes?.vetting_rate || 0}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs text-green-600 font-medium">Work Output</p>
                          <p className="text-2xl font-bold text-green-800">{teacherStats.termly_stats?.work_output?.weeks_verified || 0}/{teacherStats.termly_stats?.work_output?.weeks_with_entries || 0}</p>
                          <p className="text-xs text-green-600">Verified/Submitted</p>
                          <div className="mt-1 bg-green-200 rounded-full h-1.5">
                            <div 
                              className="bg-green-600 h-1.5 rounded-full" 
                              style={{ width: `${teacherStats.termly_stats?.work_output?.verification_rate || 0}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Weekly Lesson Notes Stats */}
                      <div className="bg-white border rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Weekly Lesson Notes (Last 5 Weeks)</p>
                        <div className="grid grid-cols-5 gap-1 text-center">
                          {(teacherStats.weekly_lesson_stats || []).map((week) => (
                            <div key={week.week} className="bg-gray-50 rounded p-2">
                              <p className="text-xs text-gray-500">Wk {week.week}</p>
                              <p className="text-sm font-bold text-blue-700">{week.vetted}</p>
                              <p className="text-[10px] text-gray-400">of {week.submitted}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Weekly Work Output Verification */}
                      <div className="bg-white border rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Weekly Work Output Verification</p>
                        <div className="grid grid-cols-5 gap-1 text-center">
                          {(teacherStats.weekly_work_output || []).map((week) => (
                            <div key={week.week} className={`rounded p-2 ${week.verified ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                              <p className="text-xs text-gray-500">Wk {week.week}</p>
                              {week.verified ? (
                                <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                              ) : week.entries > 0 ? (
                                <Clock className="h-4 w-4 text-amber-500 mx-auto" />
                              ) : (
                                <span className="text-gray-300 text-xs">-</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Term Info */}
                      <p className="text-xs text-gray-400 text-center">
                        {teacherStats.termly_stats?.term_name} • Week {teacherStats.current_week}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <FileText className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">No performance data available</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Subscription Information (for Teachers - non DD-Supervision view) */}
              {selectedProfile.type === 'teacher' && user?.role !== 'dd_supervision' && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Lesson Notes Subscription</h3>
                  {selectedProfile.subscription ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-blue-600">Plan Type</p>
                          <p className="font-semibold text-blue-800">{selectedProfile.subscription.plan_type || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-blue-600">Class Level</p>
                          <p className="font-semibold text-blue-800">{selectedProfile.subscription.class_level || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-blue-600">Subjects</p>
                          <p className="font-semibold text-blue-800">
                            {selectedProfile.subscription.subjects?.length > 0 
                              ? selectedProfile.subscription.subjects.join(', ') 
                              : 'All subjects'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-blue-600">Status</p>
                          <Badge className={`${selectedProfile.subscription.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {selectedProfile.subscription.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                      {selectedProfile.subscription.expires_at && (
                        <p className="text-xs text-gray-500 mt-3">
                          Expires: {new Date(selectedProfile.subscription.expires_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <BookOpen className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">No active subscription</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Signature & Stamp (for Headteacher) */}
              {selectedProfile.type === 'headteacher' && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Official Credentials</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-500 mb-2">Signature</p>
                      {selectedProfile.signature ? (
                        <img 
                          src={`${API_BASE}${selectedProfile.signature}`} 
                          alt="Signature"
                          className="max-h-20 mx-auto object-contain"
                        />
                      ) : (
                        <p className="text-gray-400 text-sm italic">Not uploaded</p>
                      )}
                    </div>
                    <div className="border rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-500 mb-2">Official Stamp</p>
                      {selectedProfile.stamp ? (
                        <img 
                          src={`${API_BASE}${selectedProfile.stamp}`} 
                          alt="Stamp"
                          className="max-h-20 mx-auto object-contain"
                        />
                      ) : (
                        <p className="text-gray-400 text-sm italic">Not uploaded</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <Button onClick={closeProfileModal} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Secretary Application View Modal - Full Document Viewer */}
      {showApplicationModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowApplicationModal(false); setApplicationDocuments(null); }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">
                    {selectedApplication.application_type?.replace(/_/g, ' ').toUpperCase() || 'Application'}
                  </h2>
                  <p className="text-sm text-blue-100 mt-1">
                    Applicant: {selectedApplication.applicant_name || selectedApplication.name || 'N/A'} • 
                    School: {selectedApplication.school || selectedApplication.applicant_school || 'N/A'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`${
                    selectedApplication.status === 'escalated_to_secretary' ? 'bg-amber-500' :
                    selectedApplication.status === 'registered_by_secretary' ? 'bg-blue-500' :
                    'bg-green-500'
                  }`}>
                    {selectedApplication.status?.replace(/_/g, ' ')}
                  </Badge>
                  <button onClick={() => { setShowApplicationModal(false); setApplicationDocuments(null); }} className="text-white/80 hover:text-white">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* Registration Info */}
              {selectedApplication.registration_number && (
                <div className="mt-2 text-sm bg-white/20 rounded px-3 py-1 inline-block">
                  <span className="font-medium">Reg. No:</span> {selectedApplication.registration_number}
                </div>
              )}
            </div>

            {/* Document Tabs */}
            <div className="border-b bg-gray-50 px-4 flex-shrink-0">
              <div className="flex gap-1 overflow-x-auto">
                <button
                  onClick={() => setDocumentViewTab('applicant')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    documentViewTab === 'applicant'
                      ? 'border-blue-600 text-blue-600 bg-white'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FileText className="h-4 w-4 inline mr-2" />
                  Original Application
                </button>
                {applicationDocuments?.headteacher && (
                  <button
                    onClick={() => setDocumentViewTab('headteacher')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      documentViewTab === 'headteacher'
                        ? 'border-purple-600 text-purple-600 bg-white'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <User className="h-4 w-4 inline mr-2" />
                    Headteacher's Memo
                  </button>
                )}
                {applicationDocuments?.siso && (
                  <button
                    onClick={() => setDocumentViewTab('siso')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      documentViewTab === 'siso'
                        ? 'border-green-600 text-green-600 bg-white'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Shield className="h-4 w-4 inline mr-2" />
                    SISO's Memo
                  </button>
                )}
                {applicationDocuments?.attachments?.length > 0 && (
                  <button
                    onClick={() => setDocumentViewTab('attachments')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      documentViewTab === 'attachments'
                        ? 'border-amber-600 text-amber-600 bg-white'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Paperclip className="h-4 w-4 inline mr-2" />
                    Attachments ({applicationDocuments.attachments.length})
                  </button>
                )}
              </div>
            </div>

            {/* Document Content */}
            <div className="flex-1 overflow-y-auto bg-gray-100 p-4">
              {loadingDocuments ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {/* Original Application Tab */}
                  {documentViewTab === 'applicant' && (
                    <div className="bg-white rounded-lg shadow-sm mx-auto max-w-[21cm] min-h-[29.7cm] p-8" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                      <div className="prose prose-sm max-w-none">
                        {applicationDocuments?.application?.html_content ? (
                          <div dangerouslySetInnerHTML={{ __html: applicationDocuments.application.html_content }} />
                        ) : applicationDocuments?.application?.formatted_letter ? (
                          renderFormattedLetter(
                            applicationDocuments.application.formatted_letter,
                            applicationDocuments.application.signature_url,
                            applicationDocuments.application.stamp_url
                          )
                        ) : (
                          <pre className="whitespace-pre-wrap font-serif text-sm">
                            {applicationDocuments?.application?.plain_text || selectedApplication.plain_text || selectedApplication.content || 'No content available'}
                          </pre>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Headteacher's Memo Tab */}
                  {documentViewTab === 'headteacher' && applicationDocuments?.headteacher && (
                    <div className="bg-white rounded-lg shadow-sm mx-auto max-w-[21cm] min-h-[29.7cm] p-8" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                      <div className="text-center mb-6 border-b pb-4">
                        <h2 className="text-lg font-bold">FORWARDING MEMORANDUM</h2>
                        <p className="text-sm text-gray-600">From: {applicationDocuments.headteacher.info?.name || 'Headteacher'}</p>
                      </div>
                      <div className="prose prose-sm max-w-none">
                        {applicationDocuments.headteacher.memo ? (
                          applicationDocuments.headteacher.memo.trim().startsWith('<') ? (
                            <div dangerouslySetInnerHTML={{ __html: embedAssetsInHtml(
                              applicationDocuments.headteacher.memo,
                              applicationDocuments.headteacher.signature_url,
                              applicationDocuments.headteacher.stamp_url
                            ) }} />
                          ) : (
                            renderFormattedLetter(
                              applicationDocuments.headteacher.memo,
                              applicationDocuments.headteacher.signature_url,
                              applicationDocuments.headteacher.stamp_url
                            )
                          )
                        ) : (
                          <p className="text-gray-500 italic">No forwarding memo available</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* SISO's Memo Tab */}
                  {documentViewTab === 'siso' && applicationDocuments?.siso && (
                    <div className="bg-white rounded-lg shadow-sm mx-auto max-w-[21cm] min-h-[29.7cm] p-8" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                      <div className="text-center mb-6 border-b pb-4">
                        <h2 className="text-lg font-bold">ESCALATION MEMORANDUM</h2>
                        <p className="text-sm text-gray-600">From: {applicationDocuments.siso.info?.name || 'SISO'} • Circuit: {applicationDocuments.siso.info?.circuit_name || 'N/A'}</p>
                      </div>
                      <div className="prose prose-sm max-w-none">
                        {applicationDocuments.siso.memo ? (
                          applicationDocuments.siso.memo.trim().startsWith('<') ? (
                            <div dangerouslySetInnerHTML={{ __html: embedAssetsInHtml(
                              applicationDocuments.siso.memo,
                              applicationDocuments.siso.signature_url,
                              applicationDocuments.siso.stamp_url
                            ) }} />
                          ) : (
                            renderFormattedLetter(
                              applicationDocuments.siso.memo,
                              applicationDocuments.siso.signature_url,
                              applicationDocuments.siso.stamp_url
                            )
                          )
                        ) : (
                          <p className="text-gray-500 italic">No escalation memo available</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Attachments Tab */}
                  {documentViewTab === 'attachments' && applicationDocuments?.attachments && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-900">Attached Documents ({applicationDocuments.attachments.length})</h3>
                      <div className="grid gap-3">
                        {applicationDocuments.attachments.map((att, idx) => (
                          <div key={idx} className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <FileText className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{att.original_filename || att.filename || `Attachment ${idx + 1}`}</p>
                                <p className="text-xs text-gray-500">{att.file_type || 'Document'}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await authenticatedFetch(`${API_BASE}/api/application/attachment/${att.id || att.file_id}`);
                                    if (response.ok) {
                                      const blob = await response.blob();
                                      const url = URL.createObjectURL(blob);
                                      window.open(url, '_blank');
                                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                                    } else { alert('Failed to open attachment'); }
                                  } catch (e) { alert('Error opening attachment'); }
                                }}
                                className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 flex items-center gap-1 cursor-pointer"
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await authenticatedFetch(`${API_BASE}/api/application/attachment/${att.id || att.file_id}`);
                                    if (response.ok) {
                                      const blob = await response.blob();
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = att.original_filename || att.filename || 'attachment';
                                      document.body.appendChild(a);
                                      a.click();
                                      document.body.removeChild(a);
                                      setTimeout(() => URL.revokeObjectURL(url), 5000);
                                    }
                                  } catch (e) { alert('Error downloading'); }
                                }}
                                className="px-3 py-1.5 text-sm bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 flex items-center gap-1 cursor-pointer"
                              >
                                <Download className="h-4 w-4" />
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3 sm:p-4 border-t bg-gray-50 flex justify-between flex-shrink-0">
              <Button variant="outline" onClick={() => { setShowApplicationModal(false); setApplicationDocuments(null); }}>
                Close
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  let printTitle = 'Document';
                  let printHtml = '';
                  let sigUrl = null;
                  let stampUrl = null;

                  if (documentViewTab === 'applicant') {
                    printTitle = `${selectedApplication.application_type || 'Application'} - ${selectedApplication.applicant_name || 'Applicant'}`;
                    printHtml = applicationDocuments?.application?.html_content || '';
                    if (!printHtml && applicationDocuments?.application?.formatted_letter) {
                      printHtml = `<pre style="white-space:pre-wrap;font-family:Georgia,serif;">${applicationDocuments.application.formatted_letter}</pre>`;
                    }
                    if (!printHtml) printHtml = `<pre style="white-space:pre-wrap;font-family:Georgia,serif;">${applicationDocuments?.application?.plain_text || selectedApplication.plain_text || ''}</pre>`;
                    sigUrl = applicationDocuments?.application?.signature_url;
                    stampUrl = applicationDocuments?.application?.stamp_url;
                  } else if (documentViewTab === 'headteacher') {
                    printTitle = `Headteacher Memo - ${applicationDocuments?.headteacher?.info?.name || 'Headteacher'}`;
                    const htMemo = applicationDocuments?.headteacher?.memo_html || applicationDocuments?.headteacher?.memo || '';
                    printHtml = htMemo.trim().startsWith('<') ? htMemo : `<pre style="white-space:pre-wrap;font-family:Georgia,serif;">${htMemo}</pre>`;
                    sigUrl = applicationDocuments?.headteacher?.signature_url;
                    stampUrl = applicationDocuments?.headteacher?.stamp_url;
                  } else if (documentViewTab === 'siso') {
                    printTitle = `SISO Memo - ${applicationDocuments?.siso?.info?.name || 'SISO'}`;
                    const sisoMemo = applicationDocuments?.siso?.memo_html || applicationDocuments?.siso?.memo || '';
                    printHtml = sisoMemo.trim().startsWith('<') ? sisoMemo : `<pre style="white-space:pre-wrap;font-family:Georgia,serif;">${sisoMemo}</pre>`;
                    sigUrl = applicationDocuments?.siso?.signature_url;
                    stampUrl = applicationDocuments?.siso?.stamp_url;
                  } else if (documentViewTab === 'attachments') {
                    toast.info('Use the View/Download buttons on each attachment');
                    return;
                  }

                  if (!printHtml) { toast.error('No content to print'); return; }

                  // Embed assets as base64 for reliable printing
                  printHtml = await embedAssetsForPrint(printHtml, sigUrl, stampUrl);
                  
                  // Also convert any remaining <img src="https://..."> to base64
                  const imgRegex = /<img[^>]+src="(https?:\/\/[^"]+)"[^>]*>/gi;
                  let match;
                  const imgPromises = [];
                  const imgUrls = [];
                  while ((match = imgRegex.exec(printHtml)) !== null) {
                    const url = match[1];
                    if (!url.startsWith('data:')) {
                      imgUrls.push(url);
                      imgPromises.push(fetchImageAsBase64(url));
                    }
                  }
                  if (imgPromises.length > 0) {
                    const b64Results = await Promise.all(imgPromises);
                    for (let i = 0; i < imgUrls.length; i++) {
                      if (b64Results[i]) {
                        printHtml = printHtml.split(imgUrls[i]).join(b64Results[i]);
                      }
                    }
                  }

                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`<!DOCTYPE html><html><head>
                    <title>${printTitle}</title>
                    <style>
                      * { margin: 0; padding: 0; box-sizing: border-box; }
                      body { font-family: 'Times New Roman', Times, serif; font-size: 14pt; line-height: 1.6; max-width: 8.5in; margin: 0 auto; padding: 0.5in 0.75in; }
                      h1,h2,h3 { font-size: 16pt; margin: 0.5em 0; }
                      p { margin: 0 0 0.6em 0; }
                      strong { font-weight: bold; }
                      u { text-decoration: underline; }
                      img { max-width: 200px; max-height: 80px; }
                      @media print { body { margin: 0; padding: 0.4in 0.6in; } @page { margin: 0.4in; size: A4; } }
                    </style>
                  </head><body>${printHtml}</body></html>`);
                  printWindow.document.close();
                  printWindow.print();
                }}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Preview
              </Button>
              {selectedApplication.status === 'escalated_to_secretary' && (
                <Button
                  onClick={() => {
                    setShowApplicationModal(false);
                    setApplicationDocuments(null);
                    openRegisterModal(selectedApplication);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Register
                </Button>
              )}
              {selectedApplication.status === 'registered_by_secretary' && (
                <Button
                  onClick={() => fileToDirector(selectedApplication)}
                  disabled={processingAppAction}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  File to Director
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Secretary Registration Modal */}
      {showRegisterModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRegisterModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-t-xl">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Register Application
              </h2>
              <p className="text-sm text-blue-100 mt-1">
                {selectedApplication.application_type?.replace(/_/g, ' ')}
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Applicant Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Applicant:</span> {selectedApplication.applicant_name || selectedApplication.name || 'N/A'}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">School:</span> {selectedApplication.school || selectedApplication.applicant_school || 'N/A'}
                </p>
              </div>

              {/* Registration Number Input */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Registration Number <span className="text-red-500">*</span>
                </label>
                <Input
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                  placeholder="e.g., REG/2026/001"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the official registration number for this application
                </p>
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Notes (Optional)
                </label>
                <Textarea
                  value={registrationNotes}
                  onChange={(e) => setRegistrationNotes(e.target.value)}
                  placeholder="Any additional notes for this registration..."
                  className="min-h-[80px] resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t p-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowRegisterModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={registerApplication}
                disabled={!registrationNumber.trim() || processingAppAction}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {processingAppAction ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Register Application
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
